import {
  ScaleMultipliers,
  type AudioFrame,
  type BuildData,
  type CachedScreenData,
  type CompanyData,
  type DeviceProviderSettings,
  type Dimensions,
  type EmailPayload,
  type EnvVar,
  type GetCachedScreenAPIResults,
  type InputValue,
  type InteractionLog,
  type NetworkLog,
  type PixelsToCrop,
  type PromptStep,
  type RandomValueIndexes,
  type RandomValuesTemplates,
  type RunExecutionData,
  type SettingsData,
  type TemplateWithLabel,
} from "./types";
import { getCompanyIdFromUpload } from "./jiraHelpers";
import { addDoc, collection, getDocs, query, where } from "@firebase/firestore";
import { customFirestore, customStorage } from "../firebase";
import * as Sentry from "@sentry/react";
import { DateTime } from "luxon";
import {
  BACKEND_SERVER_URL,
  FASTAPI_SERVER_URL,
  LAMBDA_AWS_TIMEOUT,
  LAMBDA_AWS_URL,
} from "../constants/aws-constants";
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import { type Node } from "prosemirror-model";
import { type Selection } from "prosemirror-state";
import * as amplitude from "@amplitude/analytics-browser";

import { getSchema } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  kAndroidOsName,
  kDefaultAppetizeOSVersions,
  kIosOsName,
} from "@/constants/appetize-constants";
import UniqueID from "@tiptap/extension-unique-id";
import { Timestamp } from "firebase/firestore";
import Mention from "@tiptap/extension-mention";
import {
  filterNonEssentialUIData,
  getVisibleItems,
  transformUIData,
  type TreeNode,
} from "@/routes/sessions/DeviceContext";
import { type MutableRefObject } from "react";
import { appiumSessionMutationAtom } from "./appium-device";
import { sessionMutationAtom } from "./simulator";
import { type Subscriber } from "rxjs";
import { UiState } from "@/components/tiptap-extension/ui-state-extension";

const b64toBlob = async (
  base64: string,
  type = "application/octet-stream"
): Promise<Blob> => await fetch(base64).then(async (res) => await res.blob());

const removeItemFromArray = (array: any[], index: number): any[] => {
  // If the item is not found, return the original array
  if (index === -1) {
    return array;
  }

  // Create a new array with the item removed
  const newArray = [...array.slice(0, index), ...array.slice(index + 1)];

  // Return the new array
  return newArray;
};

const replaceItemFomArray = (
  array: any[],
  index: number,
  value: any
): any[] => {
  return [...array.slice(0, index), value, ...array.slice(index + 1)];
};

const capitalizeFirstLetter = (word: string): string => {
  if (word.length === 0) {
    return word;
  }
  return word.charAt(0).toUpperCase() + word.slice(1);
};

const delay = async (milliseconds: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, milliseconds));
};

const getImageDimensions = async (
  base64Image: string
): Promise<{ width: number; height: number }> => {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const dimensions = { width: image.width, height: image.height };
      resolve(dimensions);
    };
    image.onerror = reject;
    image.src = base64Image; // Replace "jpeg" with the correct image format if necessary
  });
};

function getBase64ImageDimensions(base64: string) {
  const binary = atob(base64.split(",")[1]); // Remove data URL prefix

  // For JPEG
  if (binary.charCodeAt(0) === 0xff && binary.charCodeAt(1) === 0xd8) {
    let pos = 2;
    while (pos < binary.length) {
      if (binary.charCodeAt(pos) !== 0xff) return null;
      const marker = binary.charCodeAt(pos + 1);
      const size =
        (binary.charCodeAt(pos + 2) << 8) + binary.charCodeAt(pos + 3);

      if (
        marker >= 0xc0 &&
        marker <= 0xcf &&
        marker !== 0xc4 &&
        marker !== 0xc8
      ) {
        return {
          height:
            (binary.charCodeAt(pos + 5) << 8) + binary.charCodeAt(pos + 6),
          width: (binary.charCodeAt(pos + 7) << 8) + binary.charCodeAt(pos + 8),
        };
      }
      pos += 2 + size;
    }
  }

  // For PNG
  if (binary.charCodeAt(0) === 0x89 && binary.charCodeAt(1) === 0x50) {
    return {
      width:
        (binary.charCodeAt(16) << 24) +
        (binary.charCodeAt(17) << 16) +
        (binary.charCodeAt(18) << 8) +
        binary.charCodeAt(19),
      height:
        (binary.charCodeAt(20) << 24) +
        (binary.charCodeAt(21) << 16) +
        (binary.charCodeAt(22) << 8) +
        binary.charCodeAt(23),
    };
  }

  return null;
}

const flattenArray = (arr: string[][]): string[] => {
  return arr.reduce((acc, val) => acc.concat(val), []);
};

const getTemplatesForUploadId = async (uploadId: string): Promise<any> => {
  const companyId = await getCompanyIdFromUpload(uploadId);
  const companyQuery = query(
    collection(customFirestore, "companies"),
    where("id", "==", companyId)
  );
  const result = await getDocs(companyQuery);

  if (!result.empty) {
    const templates: TemplateWithLabel[] =
      result.docs[0].data()?.templates ?? [];
    return transformTemplates(templates);
  }

  return {};
};

const transformTemplates = (templates: TemplateWithLabel[]) => {
  return templates.reduce(
    (prev, template) => ({
      ...prev,
      [template.imageUrl]: template.label,
    }),
    {}
  );
};

async function handleEmailSending(email: string) {
  const payload: EmailPayload = {
    to: "md@kitchenful.com",
    text: `test message for mail ${email}`,
    subject: "testing",
    html: "none",
  };

  const response = await fetch("https://admin.flutterboost.com/api/send-mail", {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const responseJson = response.json();
  console.log("Handling email sending POST Response: ", responseJson);

  return await responseJson;
}

const trackAmplitudeEvent = ({
  eventType,
  eventProperties,
  groups,
  groupProperties,
}: {
  eventType: string;
  eventProperties?: Record<string, any> | undefined;
  groups?: Record<string, any> | undefined;
  groupProperties?: Record<string, any> | undefined;
}) => {
  if (import.meta.env.MODE === "production") {
    amplitude.track({
      event_type: eventType,
      event_properties: eventProperties,
      group_properties: groupProperties,
      groups,
    });
  }
};

const trackSentryException = (e: any) => {
  if (import.meta.env.MODE === "production") {
    Sentry.captureException(e);
  }
};

const trackExceptionOnFirestore = async ({
  error,
  source,
}: {
  error: any;
  source: string;
}) => {
  if (import.meta.env.MODE === "production") {
    try {
      await addDoc(collection(customFirestore, "errors"), {
        error: JSON.stringify(error),
        source,
        createdAt: Timestamp.now(),
      });
    } catch (e) {
      console.error(e);
      trackSentryException(e);
    }
  }
};

const trackSentryMessage = (message: any) => {
  if (import.meta.env.MODE === "production") {
    Sentry.captureMessage(message, "debug");
  }
};

// Lightweight perf/timing helper. Wrap async code to get duration logs consistently.
let perfLogIndentLevel = 0;

const withPerfLog = async <T>(
  label: string,
  fn: () => Promise<T>,
  extra?: Record<string, any>
): Promise<T> => {
  perfLogIndentLevel = Math.max(perfLogIndentLevel, 0);
  const indent = "  ".repeat(perfLogIndentLevel);
  perfLogIndentLevel++;

  const start = performance.now();
  try {
    return await fn();
  } finally {
    const durationMs = Math.round(performance.now() - start);
    console.debug(
      `${indent}[perf] ${label} ▸ {durationMs: ${durationMs}${
        extra
          ? ", " +
            Object.entries(extra)
              .map(([k, v]) => `${k}: ${typeof v === "string" ? `'${v}'` : v}`)
              .join(", ")
          : ""
      }}`
    );
    perfLogIndentLevel--;
  }
};

const resetPerfLogIndent = () => {
  perfLogIndentLevel = 0;
};

function getDigitsAfterFirstSemicolon(input: string): string[] | null {
  const startIndex = input.indexOf(";") + 1; // find the first semicolon
  const substring = input.slice(startIndex); // get the substring starting after the first semicolon

  if (startIndex !== 0) {
    const matches = substring.match(/\d+(\.\d+)?/g); // match integers and decimal numbers
    return matches?.length === 2 ? matches : null;
  } else {
    // Look specifically for x= and y= followed by numbers
    const xMatch = substring.match(/x=(\d+(\.\d+)?)/);
    const yMatch = substring.match(/y=(\d+(\.\d+)?)/);

    // Check if both x and y values were found
    if (xMatch && yMatch) {
      return [xMatch[1], yMatch[1]];
    }

    return null;
  }
}

const getDefaultSlideStartingCoordinates = (
  direction: string,
  img_width: number,
  img_height: number
) => {
  switch (direction) {
    case "left":
      return { startX: img_width * 1, startY: img_height / 2 };
    case "right":
      return { startX: img_width * 0, startY: img_height / 2 };
    case "up":
      return { startX: img_width / 2, startY: img_height * 0.6 };
    case "down":
      return { startX: img_width / 2, startY: img_height * 0.4 };
    default:
      return { startX: img_width / 2, startY: img_height / 2 };
  }
};

const getSlideAnnotationDetails = (command: string) => {
  let action, direction, percentage, startX, startY;
  const actionMatch = command.match(/(slide|swipe|scroll)/);
  if (actionMatch != null && actionMatch.length === 2) {
    action = actionMatch[1];
  }
  const directionMatch = command.match(/(left|right|up|down)/);
  if (directionMatch != null && directionMatch.length === 2) {
    direction = directionMatch[1];
    if (action == "scroll") {
      direction = invertDirection(direction);
    }
  } else {
    console.log("No direction found for command", command);
    return null;
  }

  const coordinatesAndPercentage = command.match(/(\d+)%.*;(\d+);(\d+);/);

  if (
    coordinatesAndPercentage != null &&
    coordinatesAndPercentage.length === 4
  ) {
    percentage = Number(coordinatesAndPercentage[1]);
    startX = Number(coordinatesAndPercentage[2]);
    startY = Number(coordinatesAndPercentage[3]);
  } else {
    percentage = direction === "left" || direction === "right" ? 90 : 45;
  }

  return { direction, percentage, startX, startY };
};

const invertDirection = (initialDirection: string) => {
  switch (initialDirection) {
    case "up":
      return "down";
    case "down":
      return "up";
    case "left":
      return "right";
    case "right":
      return "left";
    default:
      return "up";
  }
};

function arrayToCsv(data: any[]) {
  return data
    .map(
      (row) =>
        row
          .map(String) // convert every value to String
          .map((v) => v.replaceAll('"', '""')) // escape double colons
          .map((v) => `"${v}"`) // quote it
          .join(",") // comma-separated
    )
    .join("\r\n"); // rows starting on new lines
}

function downloadBlob(content: any, filename: string, content_type: string) {
  // Create a blob
  const blob = new Blob([content], { type: content_type });
  const url = URL.createObjectURL(blob);

  // Create a link to download it
  const pom = document.createElement("a");
  pom.href = url;
  pom.setAttribute("download", filename);
  pom.click();
}

const hideNetworkLogsEnvVars = (logs: any[], envVars: EnvVar[]): any[] => {
  return logs.map((log: any) => {
    if (log.type === "request") {
      return {
        ...log,
        ...(log.request?.postData?.text != null && {
          request: {
            ...log.request,
            postData: {
              ...log.request.postData,
              text: hideEnvVarSecrets(log.request.postData.text, envVars),
            },
          },
        }),
      };
    } else if (log.type === "response") {
      return {
        ...log,
        ...(log.request?.postData?.text != null && {
          request: {
            ...log.request,
            postData: {
              ...log.request.postData,
              text: hideEnvVarSecrets(log.request.postData.text, envVars),
            },
          },
        }),
        ...(log.response?.content?.text != null && {
          response: {
            ...log.response,
            content: {
              ...log.response.content,
              text: hideEnvVarSecrets(log.response.content.text, envVars),
            },
          },
        }),
      };
    }

    return log;
  });
};

const hideEnvVarSecrets = (item: string, envVars: EnvVar[]) => {
  let changedString = item;

  if (envVars.length > 0) {
    for (const envVar of envVars) {
      if (envVar.isSecret === true) {
        changedString = changedString.replaceAll(
          `${envVar.value}`,
          `{{env.${envVar.key}}}`
        );
        if (changedString !== item) {
          console.log(" > Hiding env var", envVar);
          console.log(" > inside text: ", item);
        }
      }
    }
  }

  return changedString;
};

const replaceEnvVars = ({
  item,
  envVars,
  revealSecrets = false,
  executionData,
}: {
  item: string;
  envVars: EnvVar[];
  revealSecrets: boolean;
  executionData?: {
    testName: string;
    testInputKey: string;
  };
}) => {
  console.log(`Replacing ${item} with envVars`, envVars, revealSecrets);

  if (item === "extractedLink") {
    return "{{extractedLink}}";
  }
  if (envVars.length > 0) {
    const passedKey = item.trim().split("env.")[1] ?? item.trim();
    if (passedKey) {
      const foundEnvVar = envVars.find((value) => value.key === passedKey);
      if (foundEnvVar === undefined) {
        if (executionData != null) {
          throw new Error(
            `{{env.${passedKey}} doesn't exist but is defined as value for test input ${executionData.testInputKey} in test "${executionData.testName}"`
          );
        }
        throw new Error(`Env variable with key ${item} not found`);
      } else {
        if (revealSecrets || foundEnvVar.isSecret !== true) {
          return foundEnvVar.value;
        }
      }
    }
  }
};

const getLastStepNumberFrom = (actionHistory: string[]) => {
  return actionHistory.reduce((maxStep, step) => {
    const stepMatch = step.match(/\[Step (\d+)\]/);
    if (stepMatch != null) {
      const stepNumber = parseInt(stepMatch[1], 10);
      return Math.max(maxStep, stepNumber);
    }
    return maxStep;
  }, 0);
};

const getStepNumberFromGroup = (group: string[]): string | undefined =>
  group
    .find((line) => line.startsWith("step_number:"))
    ?.split("step_number:")
    .at(1)
    ?.trim();

const parseCommands = ({
  shortenCommandStringEnabled,
  shortenCommandStringFromTopEnabled,
  actionHistory,
  promptSteps,
  envVars,
  runInputValues,
  currentTemplateTimestamp,
  randomValuesTemplates,
}: {
  shortenCommandStringEnabled: boolean;
  shortenCommandStringFromTopEnabled: boolean;
  actionHistory: string[];
  promptSteps: PromptStep[];
  envVars: EnvVar[];
  runInputValues: InputValue[];
  currentTemplateTimestamp: any;
  randomValuesTemplates: MutableRefObject<RandomValuesTemplates>;
}) => {
  let parsedPromptSteps = promptSteps.map((step) => ({
    ...step,
    text: parsePrompt({
      prompt: step.text,
      envVars,
      runInputValues,
      currentTemplateTimestamp,
      randomValuesTemplates,
      stepId: step.id,
    }),
    plainText: parsePrompt({
      prompt: step.plainText,
      envVars,
      runInputValues,
      currentTemplateTimestamp,
      randomValuesTemplates,
      stepId: step.id,
    }),
  }));
  let parsedCommands = getPromptStepsText(parsedPromptSteps);

  if (shortenCommandStringEnabled) {
    const lastStepNumber = getLastStepNumberFrom(actionHistory);

    // optimize below lines
    const { shortenedCommands, shortenedPromptSteps } = shortenCommands(
      parsedCommands,
      parsedPromptSteps,
      lastStepNumber,
      false
    );
    [parsedCommands, parsedPromptSteps] = [
      shortenedCommands,
      shortenedPromptSteps,
    ];
  }

  if (shortenCommandStringFromTopEnabled) {
    const { shortenedCommands, shortenedPromptSteps } = shortenCommands(
      parsedCommands,
      parsedPromptSteps,
      0,
      true
    );
    [parsedCommands, parsedPromptSteps] = [
      shortenedCommands,
      shortenedPromptSteps,
    ];
  }
  // console.log("Parsed prompts", { parsedCommands, parsedPromptSteps });

  return { parsedCommands, parsedPromptSteps };
};

interface RunTemplateTimestampOptions {
  runId?: string;
  executionId?: string;
  attempt?: number;
}

const runTemplateTimestamps = new Map<string, string>();

const createTemplateTimestamp = (): string => `${Math.floor(Date.now() / 1000)}`;

// `{{timestamp}}` remains a Unix-seconds timestamp. It is locked per
// run/execution/attempt key so prompt rendering, cache-hit re-resolution, and
// baseline-save alignment all agree within one attempt, while retries and new
// executions re-render it from the current time.
const getRunTemplateTimestamp = ({
  runId,
  executionId,
  attempt = 0,
}: RunTemplateTimestampOptions = {}): string => {
  if (runId == null || runId.length === 0) {
    return createTemplateTimestamp();
  }
  const cacheKey = [runId, executionId ?? "", attempt].join(":");
  const cached = runTemplateTimestamps.get(cacheKey);
  if (cached != null) return cached;
  const timestamp = createTemplateTimestamp();
  runTemplateTimestamps.set(cacheKey, timestamp);
  return timestamp;
};

const parsePrompt = ({
  prompt,
  envVars,
  runInputValues,
  currentTemplateTimestamp,
  stepId,
  randomValuesTemplates,
}: {
  prompt: string;
  envVars: EnvVar[];
  runInputValues: Array<{ key: string; value: string }>;
  currentTemplateTimestamp: any;
  randomValuesTemplates?: React.MutableRefObject<RandomValuesTemplates>;
  stepId?: string;
}) => {
  const randomValueCounter: RandomValueIndexes = {};
  return prompt.replace(/{{(.*?)}}/g, (match, key) => {
    const parsedKey = key.trim();
    const replacedTemplateItem =
      replaceTemplateItem(parsedKey, envVars, runInputValues) || match;

    const randomRangeMatches = parsedKey.match(
      /randomNumberInRange\((-?\d+),(-?\d+)\)/
    );
    const randomNumberMatches = parsedKey.match(/randomNumber\((-?\d+)\)/);
    stepId = stepId ?? "temp";

    if (parsedKey === "timestamp") {
      if (currentTemplateTimestamp.current == null) {
        currentTemplateTimestamp.current = replacedTemplateItem;
      }
      return currentTemplateTimestamp.current;
    } else if (randomRangeMatches != null) {
      if (randomValuesTemplates == null) {
        return replacedTemplateItem;
      }

      const min = parseInt(randomRangeMatches[1]);
      const max = parseInt(randomRangeMatches[2]);
      const rangeKey = `${min},${max}`;
      const randomValueIndex = randomValueCounter[rangeKey] ?? 0;

      const currentValue =
        randomValuesTemplates.current[stepId]?.[rangeKey]?.[randomValueIndex];

      if (currentValue != null) {
        randomValueCounter[rangeKey] = randomValueIndex + 1;
        return currentValue;
      }

      randomValuesTemplates.current = {
        ...randomValuesTemplates.current,
        [stepId]: {
          ...randomValuesTemplates.current[stepId],
          [rangeKey]: {
            ...randomValuesTemplates.current[stepId]?.[rangeKey],
            [randomValueIndex]: parseInt(replacedTemplateItem),
          },
        },
      };
      randomValueCounter[rangeKey] = randomValueIndex + 1;
      return replacedTemplateItem;
    } else if (randomNumberMatches != null) {
      const digits = parseInt(randomNumberMatches[1]);
      const randomValueIndex = randomValueCounter[`${digits}`] ?? 0;

      const currentValue =
        randomValuesTemplates.current[stepId]?.[`${digits}`]?.[
          randomValueIndex
        ];
      if (currentValue != null) {
        randomValueCounter[`${digits}`] = randomValueIndex + 1;
        return currentValue;
      }

      randomValuesTemplates.current = {
        ...randomValuesTemplates.current,
        [stepId]: {
          ...randomValuesTemplates.current[stepId],
          [`${digits}`]: {
            ...randomValuesTemplates.current[stepId]?.[`${digits}`],
            [randomValueIndex]: parseInt(replacedTemplateItem),
          },
        },
      };
      randomValueCounter[`${digits}`] = randomValueIndex + 1;
    }
    return replacedTemplateItem;
  });
};

const parseMarkdownTemplate = ({
  markdown,
  envVars,
  runInputValues,
  currentTemplateTimestamp,
  randomValuesTemplates,
}: {
  markdown: string;
  envVars: EnvVar[];
  runInputValues: Array<{ key: string; value: string }>;
  currentTemplateTimestamp: any;
  randomValuesTemplates: React.MutableRefObject<RandomValuesTemplates>;
}) => {
  return parsePrompt({
    prompt: markdown,
    envVars,
    runInputValues,
    currentTemplateTimestamp,
    randomValuesTemplates,
    stepId: "markdown-global-task",
  });
};

const replaceTemplateItem = (
  item: string,
  envVars: EnvVar[] = [],
  runInputValues: Array<{ key: string; value: string }> = []
) => {
  if (item === "extractedLink") {
    return "{{extractedLink}}";
  }
  if (item === "currentDate") {
    return DateTime.now().toUTC().toFormat("y-MM-dd");
  }
  if (item === "currentDateFormatted") {
    return getFormattedDate(new Date());
  }
  const currentDateTimeRegex = /datetime\(([+-]?\d+(?:\.\d+)?)\)/;
  if (currentDateTimeRegex.test(item)) {
    const match = item.match(currentDateTimeRegex);
    const hoursOffset = match![1] ? parseFloat(match![1]) : 0;

    return getISO8601Date({ dateTime: DateTime.now(), offset: hoursOffset });
  }
  if (item === "timestamp") {
    return createTemplateTimestamp();
  }

  const randomRangeMatches = item.match(
    /randomNumberInRange\((-?\d+),(-?\d+)\)/
  );
  if (randomRangeMatches != null) {
    const min = parseInt(randomRangeMatches[1]);
    const max = parseInt(randomRangeMatches[2]);
    const randomNumber = generateRandomInRange(min, max);
    return `${randomNumber}`;
  }

  const randomNumberMatches = item.match(/randomNumber\((-?\d+)\)/);
  if (randomNumberMatches != null) {
    const digits = parseInt(randomNumberMatches[1]);
    const randomNumber = generateRandomNumber(digits);
    return `${randomNumber}`;
  }

  if (item.startsWith("env.")) {
    return replaceEnvVars({ item, envVars, revealSecrets: false });
  }
  const runInputKeys = runInputValues.map((input) => input.key);
  if (runInputKeys.includes(item)) {
    return runInputValues.find((input) => input.key === item)?.value;
  }
};

const replaceEnvVarsAndRunInputs = ({
  command,
  inputValues = [],
  envVars = [],
}: {
  command: string;
  envVars?: EnvVar[];
  inputValues?: InputValue[];
}) => {
  return command.replace(/{{(.*?)}}/g, (match, key) => {
    const item = key.trim();
    if (item.startsWith("env.")) {
      return replaceEnvVars({ item, envVars, revealSecrets: false }) ?? match;
    }
    const runInputKeys = inputValues.map((input) => input.key);
    if (runInputKeys.includes(item)) {
      const value = inputValues.find((input) => input.key === item)?.value;
      if (value === undefined) return;
      try {
        return value.replace(
          /{{(.*?)}}/g,
          (match, key) =>
            replaceEnvVars({
              item: key,
              envVars,
              revealSecrets: true,
            }) ?? ""
        );
      } catch (e) {
        console.error("Error replacing env vars", e);
      }
    }
    return match;
  });
};

const replacePreRequestRememberedValues = ({
  allowedValues,
  command,
  previousInteraction,
}: {
  allowedValues: string[];
  command: string;
  previousInteraction: InteractionLog;
}): string =>
  allowedValues
    .filter((allowedValue) => {
      const parsedKey = allowedValue.split(".").at(-1);
      return command.includes(`{{${parsedKey}}}`);
    })
    .reduce((prev, current) => {
      const newValue = previousInteraction.preRequestResponse?.data?.[current];
      if (newValue == null) {
        return prev;
      }
      return prev.replace(`{{${current}}}`, newValue);
    }, command);

const generateRandomNumber = (digits: number): number => {
  if (digits <= 0) {
    throw new Error("Number of digits must be greater than 0");
  }

  const min = Math.pow(10, digits - 1); // Smallest number with the specified digits
  const max = Math.pow(10, digits) - 1; // Largest number with the specified digits

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const generateRandomInRange = (min: number, max: number): number => {
  if (min > max) {
    throw new Error(
      "Minimum value must be less than or equal to maximum value"
    );
  }

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getStringSimilarity = (s1: string, s2: string) => {
  let longer = s1;
  let shorter = s2;
  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }
  const longerLength = longer.length;
  if (longerLength == 0) {
    return 1.0;
  }
  const result =
    (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
  return result;
};

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();

  const costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i == 0) costs[j] = j;
      else {
        if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) != s2.charAt(j - 1))
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

const allStringsEqual = (array: string[]) => {
  const firstElement = array[0];
  return array
    .slice(1)
    .every(
      (elem) =>
        getStringSimilarity(elem.toLowerCase(), firstElement.toLowerCase()) >=
        0.95
    );
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};

function getPropByString(obj: any, propString: string) {
  if (!propString) return obj;

  let prop;
  const props = propString.split(".");

  for (var i = 0, iLen = props.length - 1; i < iLen; i++) {
    prop = props[i];

    const candidate = obj[prop];
    if (candidate !== undefined) {
      obj = candidate;
    } else {
      break;
    }
  }
  return obj[props[i]];
}

const drawCircleOnCanvas = (
  ctx: CanvasRenderingContext2D,
  circleCenterX: number,
  circleCenterY: number,
  circleRadius: number
) => {
  // Create radial gradient
  const grd = ctx.createRadialGradient(
    circleCenterX,
    circleCenterY,
    0,
    circleCenterX,
    circleCenterY,
    circleRadius
  );
  grd.addColorStop(0, "rgba(255, 255, 0, 1)"); // Yellow color at the center
  grd.addColorStop(1, "rgba(255, 255, 0, 0)"); // Transparent at the edge

  // Set the fill style to the gradient
  ctx.fillStyle = grd;

  // Draw the circle at the specified location
  ctx.beginPath();
  ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, 2 * Math.PI);
  ctx.fill();

  // Set the fill style to solid red
  ctx.fillStyle = "rgba(255, 0, 0, 1)"; // Red

  // Draw a small red circle in the middle of the yellow circle
  ctx.beginPath();
  ctx.arc(circleCenterX, circleCenterY, 6, 0, 2 * Math.PI);
  ctx.fill();
};

const drawLineOnCanvas = (
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  direction: "left" | "right" | "up" | "down",
  percentage: number
) => {
  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;

  let endX = startX;
  let endY = startY;

  switch (direction) {
    case "left":
      endX = startX - canvasWidth * percentage;
      break;
    case "right":
      endX = startX + canvasWidth * percentage;
      break;
    case "up":
      endY = startY - canvasHeight * percentage;
      break;
    case "down":
      endY = startY + canvasHeight * percentage;
      break;
  }

  // Create a linear gradient for the line
  const lineGradient = ctx.createLinearGradient(startX, startY, endX, endY);
  lineGradient.addColorStop(0, "rgba(255, 255, 0, 0)"); // Transparent color at the start
  lineGradient.addColorStop(1, "rgba(255, 255, 0, 1)"); // Yellow at the end

  // Set the line style
  ctx.strokeStyle = lineGradient;
  ctx.lineWidth = 8; // Line width of 4 pixels

  // Draw the line
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.stroke();

  return { endX, endY };
};

// Helper function to draw a circle
async function drawShapeAndReturnImage(
  base64Image: string,
  circle: { centerX: number; centerY: number; radius: number } | null = null,
  line: {
    startX: number;
    startY: number;
    direction: "left" | "right" | "up" | "down";
    percentage: number;
  } | null = null
): Promise<string> {
  // Create new canvas and context
  const canvas = document.createElement("canvas");
  const ctx: CanvasRenderingContext2D = canvas.getContext("2d");

  // Create new image
  const img = new Image();
  img.crossOrigin = "*";
  // Convert base64 to Image
  img.src = base64Image;

  return await new Promise((resolve, reject) => {
    img.onload = function () {
      // Set the canvas to the same dimensions as the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image onto the canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      if (line != null) {
        if (line.startX === undefined || line.startY === undefined) {
          const { startX, startY } = getDefaultSlideStartingCoordinates(
            line.direction,
            canvas.width,
            canvas.height
          );
          line.startX = startX;
          line.startY = startY;
        }
        const { endX, endY } = drawLineOnCanvas(
          ctx,
          line.startX,
          line.startY,
          line.direction,
          line.percentage
        );
        drawCircleOnCanvas(ctx, endX, endY, 35);
      }
      if (circle != null) {
        drawCircleOnCanvas(ctx, circle.centerX, circle.centerY, circle.radius);
      }

      // Convert the canvas image to Base64
      const modifiedBase64Image =
        "data:image/png;base64," + canvas.toDataURL("image/png").split(",")[1];

      resolve(modifiedBase64Image);
    };

    img.onerror = reject;
  });
}

const annotateScreenshot = async (
  base64Input: string,
  commands: string[]
): Promise<string> => {
  // Annotate screenshot with tap / swipe indicator

  let image = base64Input;
  for (const command of commands.filter(
    (value) =>
      !value.includes("reasoning") &&
      !value.includes("task complete") &&
      !value.includes("error detected")
  )) {
    const isTappingCommand =
      command.includes("tabOn") ||
      command.includes("double tap") ||
      command.includes("long press") ||
      command.includes("tap at");
    const isSwipingCommand =
      command.includes("slide") ||
      command.includes("swipe") ||
      command.includes("scroll");

    try {
      if (isTappingCommand) {
        const coordsMatch = getDigitsAfterFirstSemicolon(command);
        if (coordsMatch != null) {
          const [centerX, centerY] = coordsMatch;
          const circle = {
            centerX: Number(centerX),
            centerY: Number(centerY),
            radius: 50,
          };
          image = await drawShapeAndReturnImage(image, circle, null);
        } else {
          console.log("No coordinates found for command", command);
        }
      } else if (isSwipingCommand) {
        const slideDetails = getSlideAnnotationDetails(command);
        if (slideDetails != null) {
          const { direction, percentage, startX, startY } = slideDetails;
          const line = {
            startX,
            startY,
            direction,
            percentage: percentage / 100,
          };
          image = await drawShapeAndReturnImage(image, null, line);
        } else {
          console.log("No coordinates found for command", command);
        }
      }
    } catch (error) {
      trackSentryException(error);
      console.log("Error while processing command", command);
      console.error(error);
    }
  }

  return image;
};

async function base64ToImageData(base64: string): Promise<ImageData> {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);
      resolve(ctx.getImageData(0, 0, img.width, img.height));
    };
    img.onerror = reject;
    img.src = base64;
    img.crossOrigin = "*";
  });
}

function groupArrayElements(arr, size) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

const resizeImage = async (
  base64image: string,
  new_width: number,
  new_height: number
): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = new_width;
      canvas.height = new_height;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(image, 0, 0, new_width, new_height);

      // convert the canvas back to a base64 string
      const croppedBase64Image =
        "data:image/jpg;base64," + canvas.toDataURL("image/jpg").split(",")[1];

      resolve(croppedBase64Image);
    };
    image.onerror = reject;
    image.src = base64image;
    image.crossOrigin = "*";
  });
};

// Single pixel pass shared by compareImages/compareStepScreenshots: counts
// pixels whose summed-channel difference exceeds each threshold. `isSettled`
// lets callers break out early once every outcome they care about is decided.
function countDifferingPixels(
  imageData1: ImageData,
  imageData2: ImageData,
  thresholds: number[],
  isSettled?: (counts: number[]) => boolean
): number[] {
  const pixelValuesGroupedImage1 = groupArrayElements(imageData1.data, 4);
  const pixelValuesGroupedImage2 = groupArrayElements(imageData2.data, 4);

  const counts = thresholds.map(() => 0);
  for (let i = 0; i < pixelValuesGroupedImage1.length; i++) {
    const summedIn1 = pixelValuesGroupedImage1[i].reduce(
      (partialSum: number, a: number) => partialSum + a,
      0
    );
    const summedIn2 = pixelValuesGroupedImage2[i].reduce(
      (partialSum: number, a: number) => partialSum + a,
      0
    );
    const difference = Math.abs(summedIn1 - summedIn2);

    for (let t = 0; t < thresholds.length; t++) {
      if (difference > thresholds[t]) {
        counts[t]++;
      }
    }

    if (isSettled?.(counts)) {
      break;
    }
  }
  return counts;
}

async function compareImages(
  base64Image1: string,
  base64Image2: string,
  diffThreshold: number = 0
) {
  const { imageData1, imageData2, wereDifferent } = await loadAndEqualizeImages(
    base64Image1,
    base64Image2
  );
  const pixelsDiffThreshold = wereDifferent ? 128 : diffThreshold;

  const [numberOfDifferingPixels] = countDifferingPixels(
    imageData1,
    imageData2,
    [pixelsDiffThreshold],
    ([count]) => {
      if (count > 200) {
        console.log("Breaking early due to too many differing pixels");
        return true;
      }
      return false;
    }
  );
  return numberOfDifferingPixels;
}

// Per-step previous-vs-current screen comparison: the same-screen failsafe
// needs exact equality (threshold 0) while the currentAndPreviousScreenMatch
// prompt flag tolerates anti-aliasing/clock jitter (threshold 50), so both
// are counted in a single decode + pixel pass.
async function compareStepScreenshots(
  base64Image1: string,
  base64Image2: string
): Promise<{ strictEqual: boolean; lenientMatch: boolean }> {
  const { imageData1, imageData2, wereDifferent } = await loadAndEqualizeImages(
    base64Image1,
    base64Image2
  );
  const strictThreshold = wereDifferent ? 128 : 0;
  const lenientThreshold = wereDifferent ? 128 : 50;

  const LENIENT_MATCH_PIXEL_CUTOFF = 200;
  const [strictDiffCount, lenientDiffCount] = countDifferingPixels(
    imageData1,
    imageData2,
    [strictThreshold, lenientThreshold],
    ([strict, lenient]) => strict > 0 && lenient >= LENIENT_MATCH_PIXEL_CUTOFF
  );

  return {
    strictEqual: strictDiffCount === 0,
    lenientMatch: lenientDiffCount < LENIENT_MATCH_PIXEL_CUTOFF,
  };
}

const loadAndEqualizeImages = async (
  base64Image1: string,
  base64Image2: string
) => {
  let imageData1 = await base64ToImageData(base64Image1);
  let imageData2 = await base64ToImageData(base64Image2);
  let wereDifferent = false;

  if (
    imageData1.width !== imageData2.width ||
    imageData1.height !== imageData2.height
  ) {
    const message = `Images are not the same size! ${imageData1.width}x${imageData1.height} vs ${imageData2.width}x${imageData2.height}`;
    console.log(message);
    const newWidth = Math.max(imageData1.width, imageData2.width);
    const newHeight = Math.max(imageData1.height, imageData2.height);
    console.log("Resizing to dimensions", { newWidth, newHeight });
    const resizedImage1 = await resizeImage(base64Image1, newWidth, newHeight);
    const resizedImage2 = await resizeImage(base64Image2, newWidth, newHeight);
    imageData1 = await base64ToImageData(resizedImage1);
    imageData2 = await base64ToImageData(resizedImage2);
    wereDifferent = true;
  }

  return { imageData1, imageData2, wereDifferent };
};

async function loadAndCropImage(
  base64Image: string,
  topPixelsToCrop: number = 0,
  rightPixelsToCrop: number = 0,
  bottomPixelsToCrop: number = 0,
  leftPixelsToCrop: number = 0,
  contrast: number = 1
): Promise<string> {
  return await new Promise((resolve, reject) => {
    // create an image
    const img = new Image();
    img.onload = () => {
      // create a canvas and get the context
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // adjust the canvas size considering the cropping
      canvas.width = img.width - rightPixelsToCrop - leftPixelsToCrop;
      canvas.height = img.height - topPixelsToCrop - bottomPixelsToCrop;

      // draw the image to the canvas, cropping the top, right, bottom and left pixels
      ctx.drawImage(
        img,
        leftPixelsToCrop,
        topPixelsToCrop,
        img.width - rightPixelsToCrop - leftPixelsToCrop,
        img.height - topPixelsToCrop - bottomPixelsToCrop,
        0,
        0,
        img.width - rightPixelsToCrop - leftPixelsToCrop,
        img.height - topPixelsToCrop - bottomPixelsToCrop
      );

      // Get the image data from the canvas
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;

      // Loop through the image pixels and adjust the contrast
      for (let i = 0; i < data.length; i += 4) {
        for (let j = 0; j < 3; j++) {
          // Loop over R, G, B (and not A)
          // Adjust contrast and clamp the value between 0 and 255
          data[i + j] = Math.max(
            0,
            Math.min(255, (data[i + j] - 128) * contrast + 128)
          );
        }
      }

      // Put the adjusted image data back onto the canvas
      ctx.putImageData(imgData, 0, 0);

      // convert the canvas back to a base64 string
      const croppedBase64Image =
        "data:image/jpg;base64," + canvas.toDataURL("image/jpg").split(",")[1];

      resolve(croppedBase64Image);
    };
    img.onerror = reject;
    img.src = base64Image;
    img.crossOrigin = "*";
  });
}

const callEmailReaderAPI = async (
  email: string,
  linkPattern: string
): Promise<string> => {
  const requestObject = {
    recipient_mail: email,
    link_pattern: linkPattern,
  };
  console.log("Email reader request object: ", requestObject);

  const url =
    "https://l2et4uyql5bzn5gg65mextsdeq0kqqym.lambda-url.us-east-1.on.aws/";

  const response_start_time = new Date().getTime();
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(requestObject),
  });

  if (!response.ok) {
    console.log("Incorrect response: ", response);
    // console.log("response json: ", await response.json());
    console.log("response text: ", await response.text());
    throw new Error(`HTTP error ${response.status}`);
  }

  const jsonResponse = await response.json();
  console.log("Email reader json response", jsonResponse);

  return jsonResponse.link;
};

const fetchPlus = (
  url: string,
  options: AxiosRequestConfig = {},
  retries: number,
  serverErrorMessages?: Record<number, string>
): any => {
  const axiosOptions: AxiosRequestConfig = {
    timeout: 30000,
    ...options,
    url,
  };

  return axios
    .request(axiosOptions)
    .then((response) => {
      // Check if the response status is OK (200 range)
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        throw new Error(`HTTP error ${response.status}`);
      }
    })
    .catch((error) => {
      trackSentryException(error);
      if (retries > 0) {
        // Retry logic for network errors or timeouts
        return fetchPlus(url, options, retries - 1);
      } else if (error.response) {
        // If the error has a response (like a 404, 500, etc.), throw an error with that status
        throw new Error(
          serverErrorMessages?.[error.response.status] != null
            ? serverErrorMessages[error.response.status]
            : `HTTP error ${error.response.status}`
        );
      } else {
        // For any other errors (like network errors), throw a generic error
        throw new Error(`Network error ${error.code}`);
      }
    });
};

function wasAudioPlayingInTheLastThreeSeconds(audioEvents: AudioFrame[]) {
  if (audioEvents == null || audioEvents.length === 0) {
    return false;
  }

  const now = Date.now();
  const lastThreeSecondsAudioEvents = audioEvents.filter((audioEvent) => {
    const diff = now - audioEvent.absoluteTimestamp;
    return diff <= 3000;
  });

  return lastThreeSecondsAudioEvents.length > 0;
}

const parsePressedKeys = (pressedKeys: string[]) => {
  // TODO handle Backspaces
  const lastItem = pressedKeys.at(-1);
  let lastKeyHit = "";
  if (lastItem && ["Enter", "Tab"].includes(lastItem)) {
    lastKeyHit = ` and hit ${lastItem}`;
    pressedKeys.pop();
  }
  return pressedKeys.join("") + lastKeyHit;
};

function bufferToBase64(buffer) {
  let binary = "";

  if (buffer instanceof Uint8Array) {
    for (const byte of buffer) {
      binary += String.fromCharCode(byte);
    }
  } else {
    // Assuming object format like {0: 255, 1: 216, ...}
    for (const key in buffer) {
      if (buffer.hasOwnProperty(key)) {
        binary += String.fromCharCode(buffer[key]);
      }
    }
  }

  return btoa(binary);
}

function uint8ArrayToBase64(bytes: Uint8Array) {
  let binary = "";
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:image/jpg;base64, ${window.btoa(binary)}`;
}

const generateEnrichedPrompts = (
  rawInteractions: string[],
  enrichedInteractions: string[]
): string[] => {
  const logContent =
    "Combined generated prompt:\n" +
    "---\n" +
    enrichedInteractions
      .map((value, index) => `${index + 1}. ${value}`)
      .join("\n") +
    "\n---\n\n" +
    "Individual action log\n" +
    "---\n" +
    rawInteractions
      .map(
        (value, index) =>
          `raw: ${value}\n` +
          `enriched: ${enrichedInteractions[index]}` +
          "\n---"
      )
      .reverse()
      .join("\n");

  // console.log(logContent)

  return enrichedInteractions.map((value, index) => value);
};

const getSlideDirectionAndPercentage = (xMove: number, yMove: number) => {
  let direction = "";
  if (Math.abs(xMove) > Math.abs(yMove)) {
    direction = xMove > 0 ? "right" : "left";
  } else {
    direction = yMove > 0 ? "down" : "up";
  }
  const percentage = Math.round(
    100 * Math.max(Math.abs(xMove), Math.abs(yMove))
  );
  return { direction, percentage };
};

const getSlideDirectionAndPercentageFromAssistant = ({
  startX,
  startY,
  endX,
  endY,
  rectWidth,
  rectHeight,
}: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  rectWidth: number;
  rectHeight: number;
}): { direction: string; percentage: number } => {
  const deltaX = endX - startX;
  const deltaY = endY - startY;

  // Determine primary axis and calculate percentage
  let direction = "";
  let percentage = 0;

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    // Horizontal swipe
    direction = deltaX > 0 ? "right" : "left";
    percentage = Math.trunc(Math.abs(deltaX / rectWidth) * 100);
  } else {
    // Vertical swipe
    direction = deltaY > 0 ? "down" : "up";
    percentage = Math.trunc(Math.abs(deltaY / rectHeight) * 100);
  }

  return { direction, percentage };
};
const getPromptAssistantDeviceCoordinates = (
  coordX: number,
  coordY: number,
  device: any,
  imageDimensions: Dimensions
) => {
  const divider = device.screen.width / imageDimensions.width;

  const [tapX, tapY] = [Math.round(coordX), Math.round(coordY)];
  const [deviceX, deviceY] = [
    Math.round(tapX / divider),
    Math.round(tapY / divider),
  ];

  return { deviceX, deviceY };
};

const getCoordinatesForDevice = (
  device: any,
  imageDimensions: Dimensions,
  cmd: string
) => {
  const coordinates = getDigitsAfterFirstSemicolon(cmd);

  if (coordinates !== null) {
    // if specific divider is specified we take it. Otherwise, we look for a fallback-platform divider.
    const divider = imageDimensions.width / device.screen.width;

    const [xPos, yPos] = coordinates;
    let deviceX = Math.ceil(Number(xPos) / divider);
    let deviceY = Math.ceil(Number(yPos) / divider);

    deviceX = Math.min(device.screen.width - 1, Math.max(0, deviceX));
    deviceY = Math.min(device.screen.height - 1, Math.max(0, deviceY));

    console.log(coordinates, divider, [xPos, yPos], deviceX, deviceY);
    return { deviceX, deviceY };
  }

  return null;
};


const filterAndroidUiHierarchy = (dump: any) => {
  const strip = (node: any) => {
    const rid = node.attributes?.["resource-id"] || "";
    if (rid.startsWith("com.android.systemui:")) return null;

    return { ...node, children: (node.children || []).map(strip).filter(Boolean) };
  };

  return dump
    .filter((e: any) => e.type === "app" && e.children?.length)
    .map((e: any) => ({ ...e, children: e.children.map(strip).filter(Boolean) }));
};

const removeNonAppUIElements = (uiElements: any, platform: string) => {
  try {
    if (platform.toLowerCase() === "android") {
      if (uiElements[0].children.length === 3) {
        // App objects are stored in the 3rd child
        uiElements[0].children = uiElements[0].children[2].children;
        return uiElements;
      } else {
        return filterAndroidUiHierarchy(uiElements);
      }
    } else {
      // App objects are stored dynamically in the object with appId == 'com.apple.springboard'
      uiElements = uiElements.filter(
        (element: any) => element.appId !== "com.apple.springboard"
      );
      return uiElements;
    }
  } catch (e) {
    console.error("removeNonAppUIElements error:", e);
    return uiElements;
  }
};

const removeDuplicateUIObjects = (objects: any[]): any[] => {
  const uniqueObjects = new Map<string, any>();

  objects.forEach((obj) => {
    // Create a signature by stringifying the object
    // This is simpler than comparing each property individually
    const signature = JSON.stringify(obj);

    // Only add to map if we haven't seen this exact object before
    if (!uniqueObjects.has(signature)) {
      uniqueObjects.set(signature, obj);
    }
  });

  // Return the unique objects as an array
  return Array.from(uniqueObjects.values());
};

const handleEditTextElements = (
  editTextElements: any[],
  applicationElement: any,
  width: number,
  height: number
) => {
  if (editTextElements.length > 0 && Boolean(applicationElement)) {
    const editTextUIWidth =
      applicationElement.bounds.x + applicationElement.bounds.width;
    const editTextUIHeight =
      applicationElement.bounds.y + applicationElement.bounds.height;

    if (editTextUIWidth !== width || editTextUIHeight !== height) {
      const multiplier = width / editTextUIWidth;

      for (const editTextElement of editTextElements) {
        editTextElement.bounds.x = Math.round(
          editTextElement.bounds.x * multiplier
        );
        editTextElement.bounds.y = Math.round(
          editTextElement.bounds.y * multiplier
        );
        editTextElement.bounds.width = Math.round(
          editTextElement.bounds.width * multiplier
        );
        editTextElement.bounds.height = Math.round(
          editTextElement.bounds.height * multiplier
        );
      }
    }
  }

  return editTextElements;
};

const prepareGetUIElements = async (
  uiElements: any,
  platform: string,
  screenshotDimensions: { width: number; height: number },
  getUIWidth: number,
  getUIHeight: number,
  settingsData: SettingsData
) => {
  let editTextElements: any[] = [];
  let applicationElement: any = null;
  let homeCalloutContainerPresent = false;
  const screenWidth = screenshotDimensions.width;
  const screenHeight = screenshotDimensions.height;

  const traverse = (jsonObj: object | string | number) => {
    if (jsonObj !== null && typeof jsonObj === "object") {
      Object.entries(jsonObj).forEach(([key, value]) => {
        if (Boolean(value) && typeof value === "object") {
          if (
            Object.hasOwn(value, "class") &&
            value.class.includes("EditText")
          ) {
            editTextElements.push(JSON.parse(JSON.stringify(jsonObj)));
          }
          if (
            applicationElement === null &&
            Object.hasOwn(value, "type") &&
            value.type === "application"
          ) {
            applicationElement = JSON.parse(JSON.stringify(jsonObj));
          }
          if (
            Object.hasOwn(value, "resource-id") &&
            Boolean(value["resource-id"].includes("homeCalloutContainer"))
          ) {
            homeCalloutContainerPresent = true;
          }

          // key is either an array index or object key
          traverse(value);
        }
      });
    }
  };
  traverse(uiElements);

  uiElements = removeNonAppUIElements(uiElements, platform);
  uiElements = transformUIData(uiElements);
  uiElements = filterNonEssentialUIData(uiElements);
  uiElements = getVisibleItems({
    elements: uiElements,
    deviceDimensions: { width: getUIWidth, height: getUIHeight },
    allowNonClickableItems: true,
    ignoredElements: settingsData.uiInspector.ignoredElements,
  });
  uiElements = getNonOccludedElements({
    items: uiElements,
  });
  uiElements = removeDuplicateUIObjects(uiElements);

  if (platform.toLowerCase() === "android") {
    editTextElements = handleEditTextElements(
      editTextElements,
      applicationElement,
      screenWidth,
      screenHeight
    );
  }

  if (getUIWidth != null && getUIHeight != null) {
    const multiplier = screenWidth / getUIWidth;
    uiElements = multiplyNumbersInUIHierarchy(uiElements, multiplier);
  }

  return {
    uiElements: editTextElements,
    unparsedUiElements: uiElements,
    homeCalloutContainerPresent,
  };
};

const uploadScreenshotToStorage = async (screenshot: string, url: string) => {
  console.log(`Uploading screenshot to path ${url}`);
  const screenshotRef = ref(customStorage, url);
  // `enrichedInteractionLogs/${testId}/screenshot-${currentIndex}-${index + 1}.jpg`
  const screenshotBlob = await b64toBlob(screenshot);
  await uploadBytes(screenshotRef, screenshotBlob);
  const downloadUrl = await getDownloadURL(
    ref(customStorage, screenshotRef.fullPath)
  );

  console.log(`Finished: ${downloadUrl}`);

  return downloadUrl;
};

const downloadAndUploadToStorage = async ({
  url,
  storagePath,
}: {
  url: string;
  storagePath: string;
}) => {
  try {
    const response = await axios.get(url, {
      responseType: "blob",
    });

    const blob = response.data;

    const storageRef = ref(customStorage, storagePath);
    const snapshot = await uploadBytes(storageRef, blob);
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      downloadURL,
      metadata: snapshot.metadata,
    };
  } catch (error) {
    console.error("Error uploading to storage:", error);
    throw error;
  }
};

const getBase64ImageResolution = async (
  base64: string
): Promise<{
  width: number;
  height: number;
}> => {
  return await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height,
      });
    };
    image.onerror = (err) => {
      reject(`Failed to load the image: ${err}`);
    };
    image.src = base64;
  });
};

const executeCurlCommand = async (
  curlCommand: string,
  organisationId: string
): Promise<AxiosResponse<any> | AxiosError> => {
  try {
    console.log("> Executing cURL command: ", curlCommand);

    // Construct the full URL for the FastAPI server endpoint
    // Assuming the endpoint accepts POST requests with a JSON body containing the cURL command
    const fullUrl = `https://tester-eu.mobileboost.io/curl`;

    const response = await axios.post(
      fullUrl,
      { command: curlCommand, organisationId },
      { timeout: 60000 }
    );

    console.log("Response from FastAPI server:", response.data);
    return response;
  } catch (error) {
    trackSentryException(error);
    console.error("Error calling FastAPI server:", error);
    return error as AxiosError;
  }
};

/**
 * The status the customer's own API answered a pre-request with.
 *
 * /curl is a proxy, so two statuses are in play and only one of them is about
 * the customer's API. When their API refuses the call, /curl answers 502 and
 * puts the real status in `upstreamStatus` - deliberately not reusing it as
 * its own status, since a 401 from their API and a 401 from ours mean opposite
 * things. This unwraps that, so a run log says the request answered 400 rather
 * than reporting our proxy's 502 at them.
 *
 * Falls back to the transport status, which is the right answer when /curl
 * itself is what failed (timed out, unreachable, refused the command).
 */
const preRequestStatusOf = (error: AxiosError): number | undefined => {
  const upstream = (error.response?.data as { upstreamStatus?: unknown })
    ?.upstreamStatus;
  return typeof upstream === "number" ? upstream : error.response?.status;
};

/**
 * Why a pre-request failed, in the words of whichever layer knows.
 *
 * An AxiosError's own message is only ever "Request failed with status code
 * N", which says nothing about what the call was for. /curl sends a `detail`
 * explaining what the failure costs the test; prefer it when it is there.
 */
const preRequestErrorMessage = (error: AxiosError): string => {
  const detail = (error.response?.data as { detail?: unknown })?.detail;
  return typeof detail === "string" && detail.length > 0
    ? detail
    : getErrorMessage(error);
};

const rememberNetworkValue = (gptStepsRef: any, value: string) => {
  const allSteps = gptStepsRef.current[gptStepsRef.current.length - 1];
  const lastStepNumber = getLastStepNumberFrom(allSteps);
  console.log("All steps before pushing", { allSteps, lastStepNumber });

  allSteps.push(
    `[Step ${lastStepNumber!}] remember: "network data value":"${value}"`
  );
};

const curlToAxios = async (
  curlCommand: string,
  withProxy: boolean = false
): Promise<AxiosResponse | AxiosError> => {
  // Extract the URL
  const urlMatches = curlCommand.match(/'?(http[s]?:\/\/[^'\s]+)'?/);
  console.log("urlMatches: ", urlMatches);
  if (urlMatches == null || urlMatches.length < 1) {
    throw new Error("Invalid curl command: Unable to extract URL");
  }
  let url = urlMatches[1]; // Note the change here
  url = url.replace('"', "").replace("'", ""); // Remove any quotes from the url
  // Extract headers
  const headers = {};
  const headerMatches = curlCommand.match(/-H '([^']+)'/g);
  if (headerMatches != null) {
    headerMatches.forEach((match) => {
      const headerContent = match.replace("-H '", "").replace("'", "");
      const headerParts = headerContent.split(":");
      if (headerParts.length === 2) {
        headers[headerParts[0].trim()] = headerParts[1].trim();
      }
    });
  }

  // Extract HTTP method
  let method = "GET"; // default to GET
  if (curlCommand.includes("-X POST")) {
    method = "POST";
  } else if (curlCommand.includes("-X DELETE")) {
    method = "DELETE";
  } // ... add other methods as needed

  // Extract data payload for POST requests
  const dataRegex = /(?:--data|-d)\s*(['"])(.*?)\1/;
  let data = null;
  if (method === "POST") {
    const dataMatch = curlCommand.match(dataRegex);
    if (dataMatch != null && dataMatch[2]) {
      data = dataMatch[2];
    }
  }

  if (withProxy) {
    url = `${FASTAPI_SERVER_URL}/cors-proxy?url=${encodeURIComponent(url)}`;
  }

  console.log("Pre request", method, url, headers, data);

  try {
    const response = await axios({
      method,
      url,
      headers,
      data,
    });
    console.log({ response });
    return response;
  } catch (error) {
    trackSentryException(error);
    console.error(error);
    return error as AxiosError;
  }
};

const getValueByPath = (
  obj: Record<string, any> | any[],
  path: string
): any => {
  const pathParts = path.split(".");
  let current: any = obj;
  for (const part of pathParts) {
    if (Array.isArray(current)) {
      const index = parseInt(part, 10);
      if (!isNaN(index)) {
        current = current[index];
      } else {
        return undefined;
      }
    } else if (current[part] === undefined) {
      return undefined;
    } else {
      current = current[part];
    }
  }
  return current;
};

interface NestedInfo {
  level: number;
  path?: string;
}

const getPromptStepsFromTipTapNode = ({
  node,
  selection,
}: {
  node: Node;
  selection?: Selection;
}) => {
  const plainText: PromptStep[] = [];

  const getTextParser = (
    node: any
  ): ((node: Node, nestedInfo: NestedInfo) => PromptStep[]) => {
    if (node.type?.name === "orderedList" || node.type === "orderedList") {
      return orderedListParser;
    } else if (node.type?.name === "bulletList" || node.type === "bulletList") {
      return bulletListParser;
    }
    return paragraphParser;
  };

  const paragraphParser = (node: any): PromptStep[] => {
    let text = "";
    node.content?.forEach((childNode: any) => {
      if (childNode.type?.name === "text" || childNode.type === "text") {
        text += childNode.text;
      } else if (
        childNode.type?.name === "hardBreak" ||
        childNode.type === "hardBreak"
      ) {
        text += "\n";
      }
    });

    return [
      {
        id: node.attrs?.id as string,
        text: text.length > 0 ? text : "",
        plainText: text.length > 0 ? text : "",
        isSubStep: false,
      },
    ];
  };

  const bulletListParser = (
    node: any,
    nestedInfo: NestedInfo
  ): PromptStep[] => {
    const parsedSteps: PromptStep[] = [];
    node.content.forEach((childNode: any) => {
      childNode.content.forEach((listItemChild: any) => {
        if (
          listItemChild.type.name === "paragraph" ||
          listItemChild.type === "paragraph"
        ) {
          const content =
            listItemChild.content?.content != null
              ? listItemChild.content.content
              : listItemChild.content;
          if (content == null) {
            return;
          }

          const subStepIndex = content.findIndex(
            (step) => step.type?.name === "mention" || step.type === "mention"
          );

          if (subStepIndex !== -1) {
            const subStep = content[subStepIndex];
            parsedSteps.push({
              id: listItemChild.attrs?.id,
              text: `    - ${subStep.attrs?.label}`,
              plainText: subStep.attrs?.label,
              testStep: {
                id: subStep.attrs?.testId,
                title: subStep.attrs?.label,
              },
              isSubStep: true,
            });
          } else {
            const [parsedParagraph] = paragraphParser(listItemChild);
            parsedSteps.push({
              id: listItemChild.attrs?.id,
              text: `    - ${parsedParagraph.plainText}`,
              plainText: parsedParagraph.plainText,
              isSubStep: true,
            });
          }
        } else {
          const parser = getTextParser(listItemChild);
          parsedSteps.push(
            ...parser(listItemChild, { level: nestedInfo.level + 1 })
          );
        }
      });
    });
    return parsedSteps;
  };

  const getListItemNumber = (
    nestedInfo: NestedInfo,
    selectionLevel: number,
    node: any
  ) => {
    if (nestedInfo.level > 0) {
      return 1;
    }
    if (selectionLevel > 0) {
      return selectionLevel + 1;
    }
    return node.attrs?.start ?? 1;
  };

  const orderedListParser = (
    node: any,
    nestedInfo: NestedInfo
  ): PromptStep[] => {
    const parsedSteps: PromptStep[] = [];
    const selectionLevel: number = selection?.$from?.path[4] ?? 0;
    let listItemNumber = getListItemNumber(nestedInfo, selectionLevel, node);

    node.content.forEach((listItemNode: any) => {
      listItemNode.content.forEach((listItemChild: any) => {
        if (
          listItemChild.type.name === "paragraph" ||
          listItemChild.type === "paragraph"
        ) {
          const content =
            listItemChild.content?.content != null
              ? listItemChild.content.content
              : listItemChild.content;
          if (content == null) {
            return;
          }

          const subStepIndex = content.findIndex(
            (step) => step.type?.name === "mention" || step.type === "mention"
          );
          if (subStepIndex !== -1) {
            const subStep = content[subStepIndex];
            parsedSteps.push({
              id: subStep.attrs?.id,
              text: `${
                nestedInfo.path != null ? `${nestedInfo.path}.` : ""
              }${listItemNumber++}. ${subStep.attrs?.label}`,
              plainText: subStep.attrs?.label,
              testStep: {
                id: subStep.attrs?.testId,
                title: subStep.attrs?.label,
              },
              isSubStep: false,
            });
          } else {
            const [parsedParagraph] = paragraphParser(listItemChild);

            parsedSteps.push({
              id: listItemChild.attrs?.id,
              text: `${
                nestedInfo.path != null ? `${nestedInfo.path}.` : ""
              }${listItemNumber++}. ${parsedParagraph.plainText}`,
              plainText: parsedParagraph.plainText,
              isSubStep: false,
            });
          }
        } else {
          const parser = getTextParser(listItemChild);
          parsedSteps.push(
            ...parser(listItemChild, {
              level: nestedInfo.level + 1,
              path: `${nestedInfo.path != null ? `${nestedInfo.path}.` : ""}${
                listItemNumber - 1
              }`,
            })
          );
        }
      });
    });
    return parsedSteps;
  };

  const parseParentContent = (childNode: Node) => {
    const parser = getTextParser(childNode);
    plainText.push(...parser(childNode, { level: 0 }));
  };

  if (selection != null) {
    const cutNode = node.cut(selection.from, selection.to);
    cutNode.content.forEach(parseParentContent);
  } else {
    node.content.forEach(parseParentContent);
  }

  return plainText.length === 0 ? [] : plainText;
};

const getGptDriverInput = (json: string): PromptStep[] => {
  try {
    return getPromptStepsFromTipTapNode({
      node: JSON.parse(json),
    });
  } catch (e) {
    console.error("error parsing stored prosemirror data", e);
    trackSentryException(e);
    return json;
  }
};

const getPromptStepsText = (steps: PromptStep[]) =>
  steps.reduce((prev, value, index) => {
    if (value.text !== "" && value.text !== "\n") {
      return `${prev}${index !== 0 ? "\n" : ""}${value.text}`;
    }
    return prev;
  }, "");

const createSimpleHtmlList = (items: string[]) => {
  // Start with the opening tag for the ordered list
  let htmlContent = `<meta charset="utf-8"><ol>`;

  // Loop through each item in the list
  for (const item of items) {
    // For each item, add a list item tag
    htmlContent += `<li>${item}</li>`;
  }

  // Close the ordered list tag
  htmlContent += `</ol>`;

  return htmlContent;
};

const timestampToDate = (timestamp: number) => {
  // TODO use me in run History component
  return DateTime.fromJSDate(new Date(timestamp * 1000)).toFormat(
    "dd-MM-yyyy hh:mm a"
  );
};

const CustomMention = Mention.extend({
  addAttributes() {
    return {
      testId: {},
      label: {},
    };
  },
});

const getTipTapExtensions = () => [
  StarterKit.configure({ link: false }),
  UniqueID.configure({
    types: ["paragraph", "mention"],
  }),
  CustomMention,
  UiState,
];

const getTipTapSchema = () => getSchema(getTipTapExtensions());

const parseGptCommandsResponse = (
  gptCommands: string[]
): {
  reasoning: string;
  commands: string;
  step_number: string;
  fromCache: boolean;
  step_description: string | undefined;
  actions_description: string | undefined;
  step_id: string | undefined;
  step_completed: boolean | undefined;
  caching_sync_index: number | null;
} => {
  let reasoning = "";
  let commands = "";
  let step_number: string = "";
  let fromCache = false;
  let step_description: string | undefined;
  let actions_description: string | undefined;
  let step_id: string | undefined;
  let step_completed: boolean | undefined;
  let caching_sync_index: number | null = null;

  for (const gptCommand of gptCommands) {
    const gptCommandLower = gptCommand.toLowerCase();

    if (gptCommandLower.includes("reasoning")) {
      reasoning = gptCommand;
    } else if (gptCommandLower.includes("step_number")) {
      step_number = gptCommand.split(":")[1].trim();
    } else if (gptCommandLower.includes("step_description")) {
      step_description = gptCommand.slice(gptCommand.indexOf(":") + 1).trim();
    } else if (gptCommandLower.includes("actions_description")) {
      actions_description = gptCommand.split(":")[1].trim();
    } else if (gptCommandLower.startsWith("caching_sync_index")) {
      // Mirror FastAPI's parse_gpt_commands (middlelayer_utils.py:67). The
      // caching-sync model emits this as a gpt-command line; we strip it from
      // `commands` so it doesn't leak into action history, and surface it as
      // a number so resyncStrictCachingIndex can trust it.
      const raw = gptCommand.split(":").slice(1).join(":").trim();
      const parsed = Number.parseInt(raw, 10);
      caching_sync_index = Number.isFinite(parsed) ? parsed : null;
    } else if (gptCommandLower.includes("step_id")) {
      step_id = gptCommand.split(":")[1].trim();
    } else if (gptCommandLower.includes("step_completed")) {
      step_completed = gptCommandLower.split(":")[1].trim() === "true";
    } else if (gptCommand === "Action taken from cache") {
      fromCache = true;
    } else {
      commands += "\n" + gptCommand;
    }
  }

  return {
    reasoning,
    commands: commands.trim(),
    step_number,
    step_description,
    actions_description,
    fromCache,
    step_id,
    step_completed,
    caching_sync_index,
  };
};

const getPromptText = (
  stepNumber: number,
  commandsText: string,
  step_description: string | undefined,
  step_id: string | undefined,
  promptSteps: PromptStep[]
) => {
  // console.log("> Getting prompt text with parameters", {
  //   stepNumber,
  //   commandsText,
  //   step_description,
  //   step_id,
  //   promptSteps,
  // });

  if (step_id && promptSteps.length > 0) {
    const foundStep = promptSteps.find((step) => step.id === step_id);
    if (foundStep != null) {
      return foundStep.text;
    }
  }

  if (step_description) {
    return step_description;
  }

  if (stepNumber > 0) {
    return commandsText.split("\n")[stepNumber - 1];
  }

  return undefined;
};

const imageUrlToBase64 = async (url: string) => {
  try {
    const response = await axios.get(url, {
      responseType: "blob",
    });

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(response.data); // Convert blob to base64
      reader.onloadend = () => {
        resolve(reader.result); // Resolve the promise with the Base64 string
      };
      reader.onerror = reject; // Reject the promise on error
    });
  } catch (error) {
    trackSentryException(error);
    console.error("Error fetching and converting image: ", error);
    throw error; // Rethrow or handle error as needed
  }
};

function calculateTotalWeight(alternatives: string[], query: string) {
  let weight = 0;
  let matchFound = false;

  for (let i = 0; i < alternatives.length; i++) {
    if (alternatives[i].startsWith(query)) {
      matchFound = true;
    }

    // Higher weights for earlier alternatives
    weight = 100 / (i + 1);
    if (matchFound) {
      break;
    }
  }

  // If the query matches, add a large bonus weight
  if (matchFound) {
    weight += 1000;
  }

  return weight;
}

const isUnusableCachedScreen = (cachedData: CachedScreenData) => {
  return (
    cachedData.gptCommand.includes("test+") ||
    (cachedData.gptCommand.includes("type") &&
      cachedData.gptCommand.includes("@example.com")) ||
    cachedData.gptCommand.includes("open deeplink:") ||
    cachedData.gptCommand.includes("execute: network request")
  );
};

// Returns a logging-safe deep copy of `value` where any data: URL or
// suspiciously long base64-shaped string is truncated to a short preview.
// Use when console.logging objects that may contain screenshots (or other
// large blobs) so DevTools doesn't show the full payload — particularly
// important when the object is mutated after log time (browsers print the
// *current* state on expand, not the snapshot at log time).
const BASE64_LOG_PREVIEW_CHARS = 64;
const BASE64_LOG_MIN_RAW_LENGTH = 200;
const BASE64_RAW_PATTERN = /^[A-Za-z0-9+/=\s]+$/;

const truncateBase64ForLog = (s: string): string => {
  if (s.startsWith("data:image/")) {
    const commaIdx = s.indexOf(",");
    if (commaIdx >= 0) {
      const header = s.slice(0, commaIdx + 1);
      const body = s.slice(commaIdx + 1);
      if (body.length <= BASE64_LOG_PREVIEW_CHARS) return s;
      return `${header}${body.slice(0, BASE64_LOG_PREVIEW_CHARS)}…[+${
        body.length - BASE64_LOG_PREVIEW_CHARS
      } chars]`;
    }
  }
  if (s.length >= BASE64_LOG_MIN_RAW_LENGTH && BASE64_RAW_PATTERN.test(s)) {
    return `${s.slice(0, BASE64_LOG_PREVIEW_CHARS)}…[+${
      s.length - BASE64_LOG_PREVIEW_CHARS
    } chars]`;
  }
  return s;
};

const redactBase64ForLog = (value: unknown, seen = new WeakSet()): unknown => {
  if (typeof value === "string") return truncateBase64ForLog(value);
  if (Array.isArray(value)) return value.map((v) => redactBase64ForLog(v, seen));
  if (value != null && typeof value === "object") {
    if (seen.has(value as object)) return "[Circular]";
    seen.add(value as object);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactBase64ForLog(v, seen);
    }
    return out;
  }
  return value;
};

// Returns true if two prompt strings are equivalent ignoring any {{...}}
// dynamic-content placeholders (e.g. "Type {{name}}" matches "Type {{username}}"
// as well as "Type "). Mirrors prompt_matches_with_dynamic_values in
// caching_utils.py on the backend.
const promptsEquivalentIgnoringDynamic = (a: string, b: string) => {
  const stripDynamic = (s: string) => s.replace(/{{(.*?)}}/g, "").trim();
  return stripDynamic(a) === stripDynamic(b);
};

// True if the test's full markdown looks like an "abstract" prompt — one
// authored as a Goal/Context/Steps document rather than as an explicit step
// list. The agent expands these into concrete steps at run time, so the
// rendered prompt for any given step can drift between baseline and replay
// even when the underlying flow is unchanged. Strict caching therefore can
// only rely on command equivalence, not prompt equivalence, for these tests.
const isAbstractMarkdownPrompt = (
  markdown: string | null | undefined
): boolean => {
  if (markdown == null || markdown.length === 0) return false;
  let hasGoal = false;
  let hasContext = false;
  for (const rawLine of markdown.split("\n")) {
    const match = /^(#{1,6})\s+(.+?)\s*$/.exec(rawLine);
    if (match == null) continue;
    const text = match[2].trim().toLowerCase();
    if (/^goal\b/.test(text)) hasGoal = true;
    else if (/^context\b/.test(text)) hasContext = true;
    if (hasGoal && hasContext) return true;
  }
  return false;
};

// Pull (x, y) coordinates out of an appetize command. Mirrors
// extract_coordinates_from_command in caching_utils.py.
const extractAppetizeCoordinates = (
  command: string
): { x: number; y: number } | null => {
  const tapMatch = command.match(/x=(\d+)\s+y=(\d+)/);
  if (tapMatch) {
    return { x: parseInt(tapMatch[1], 10), y: parseInt(tapMatch[2], 10) };
  }
  const semiMatch = command.match(/;(\d+);(\d+);?/);
  if (semiMatch) {
    return { x: parseInt(semiMatch[1], 10), y: parseInt(semiMatch[2], 10) };
  }
  return null;
};

const tabonCoordinatesWithinMargin = (
  a: string,
  b: string,
  margin: number
): boolean => {
  const ca = extractAppetizeCoordinates(a);
  const cb = extractAppetizeCoordinates(b);
  if (ca == null || cb == null) return false;
  return Math.abs(ca.x - cb.x) <= margin && Math.abs(ca.y - cb.y) <= margin;
};

// First non-empty line of a cached step's appetizeCommand, or the first entry
// in an AI response's appetizeCommands array.
const firstAppetizeCommandLine = (
  command: string | string[] | undefined | null
): string => {
  if (command == null) return "";
  if (Array.isArray(command)) return (command[0] ?? "").trim();
  return (
    command
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)[0] ?? ""
  );
};

// Mirrors _commands_match in client_session.py. Treats two tabon commands as
// equal when their coordinates are within `margin` pixels of each other, and
// task-complete / wait commands as equal regardless of arguments.
const appetizeCommandsMatchForStrictCaching = (
  cachedFirstLine: string,
  aiFirstLine: string,
  margin: number
): boolean => {
  if (cachedFirstLine.length === 0 || aiFirstLine.length === 0) return false;
  const cl = cachedFirstLine.toLowerCase();
  const al = aiFirstLine.toLowerCase();
  if (cl.includes("tabon") && al.includes("tabon")) {
    return tabonCoordinatesWithinMargin(cachedFirstLine, aiFirstLine, margin);
  }
  for (const keyword of ["task complete:", "wait:"]) {
    if (cl.includes(keyword) && al.includes(keyword)) return true;
  }
  return cachedFirstLine === aiFirstLine;
};

// Mirrors _skip_allowed in client_session.py. Returns true if every step in the
// gap between the current cached pointer and a candidate sync target is
// "skippable" — pure waits/scrolls/slides/swipes/etc. — with at most one tap.
// `go to:` is the home-button navigation primitive (see handleGoTo in
// use-command-handler.tsx); it resets app state rather than acting on UI and
// is safe to skip when searching forward for a sync target.
const STRICT_CACHING_SKIPPABLE_KEYWORDS = [
  "wait",
  "scroll",
  "slide",
  "swipe",
  "user code executed",
  "continue with next step",
  "go to:",
];

// All tap-like appetize command forms recognized in the codebase (kept in sync
// with `helpers.ts:isTappingCommand` and friends). All of these are subject to
// the "at most one skipped tap" rule in strict caching.
const STRICT_CACHING_TAP_KEYWORDS = [
  "tabon",
  "double tap",
  "long press",
  "tap at",
];

type StrictCachingSkipBucket = "tap" | "skippable" | "important";

type StrictCachingSkipBreakdown = {
  allowed: boolean;
  skippedTaps: number;
  skippedImportant: number;
  classifications: Array<{ command: string; bucket: StrictCachingSkipBucket }>;
};

const classifyStrictCachingStep = (
  step: CachedScreenData
): StrictCachingSkipBucket => {
  const lines = (step.appetizeCommand ?? "")
    .split("\n")
    .map((l) => l.toLowerCase());
  if (
    lines.some((l) => STRICT_CACHING_TAP_KEYWORDS.some((k) => l.includes(k)))
  ) {
    return "tap";
  }
  if (
    lines.some((l) =>
      STRICT_CACHING_SKIPPABLE_KEYWORDS.some((k) => l.includes(k))
    )
  ) {
    return "skippable";
  }
  return "important";
};

const strictCachingSkipBreakdown = (
  intermediateSteps: CachedScreenData[]
): StrictCachingSkipBreakdown => {
  let skippedTaps = 0;
  let skippedImportant = 0;
  const classifications: StrictCachingSkipBreakdown["classifications"] = [];
  for (const step of intermediateSteps) {
    const bucket = classifyStrictCachingStep(step);
    if (bucket === "tap") skippedTaps++;
    else if (bucket === "important") skippedImportant++;
    classifications.push({
      command: firstAppetizeCommandLine(step.appetizeCommand),
      bucket,
    });
  }
  return {
    allowed: skippedImportant === 0 && skippedTaps <= 1,
    skippedTaps,
    skippedImportant,
    classifications,
  };
};

// Walks the baseline forward from the current pointer, looking for a step whose
// prompt + first appetize command match the AI step we just took, and where
// every step we'd be jumping over is skippable. Returns the new pointer (idx + 1)
// if a sync target is found, otherwise the input pointer unchanged. Mirrors
// sync_caching_index_with_ai_response in client_session.py.
//
// If `aiCachingSyncIndex` is provided (the agent explicitly told us which
// baseline step its response corresponds to) we trust it and skip the heuristic.
//
// `isAbstract` flips the matcher into "commands-only" mode: prompt equivalence
// is no longer required, because abstract Goal/Context/Steps tests get
// re-expanded into different per-step prompts on each run. Without this the
// caching pointer can never sync and falls back to pure-AI execution.
const resyncStrictCachingIndex = ({
  cachedScreens,
  currentCachedStepIndex,
  aiPrompt,
  aiAppetizeCommands,
  aiCachingSyncIndex,
  screenWidthPx,
  isAbstract = false,
  logPrefix = "",
}: {
  cachedScreens: CachedScreenData[];
  currentCachedStepIndex: number;
  aiPrompt: string;
  aiAppetizeCommands: string[];
  aiCachingSyncIndex?: number | null;
  screenWidthPx: number;
  isAbstract?: boolean;
  logPrefix?: string;
}): number => {
  if (cachedScreens.length === 0) return currentCachedStepIndex;

  if (
    aiCachingSyncIndex != null &&
    Number.isFinite(aiCachingSyncIndex) &&
    aiCachingSyncIndex >= 0
  ) {
    const newIndex = Math.floor(aiCachingSyncIndex) + 1;
    console.log(
      `${logPrefix}> Strict caching index set by AI to ${newIndex} (cachingSyncIndex=${aiCachingSyncIndex})`
    );
    return newIndex;
  }

  const aiFirstLine = firstAppetizeCommandLine(aiAppetizeCommands);
  if (aiFirstLine.length === 0) return currentCachedStepIndex;

  const margin = Math.max(1, Math.round(0.04 * screenWidthPx));

  for (let idx = currentCachedStepIndex; idx < cachedScreens.length; idx++) {
    const step = cachedScreens[idx];
    const promptMatches = isAbstract
      ? true
      : promptsEquivalentIgnoringDynamic(step.prompt ?? "", aiPrompt ?? "");
    const wasntUsed = step.wasUsedInCachingAlready !== true;
    const cachedFirstLine = firstAppetizeCommandLine(step.appetizeCommand);
    const commandsMatch = appetizeCommandsMatchForStrictCaching(
      cachedFirstLine,
      aiFirstLine,
      margin
    );
    const intermediates = cachedScreens.slice(currentCachedStepIndex, idx);
    const skipBreakdown = strictCachingSkipBreakdown(intermediates);
    const skipOk = skipBreakdown.allowed;

    if (promptMatches && wasntUsed && commandsMatch && skipOk) {
      console.log(
        `${logPrefix}> Strict caching index synced: ${currentCachedStepIndex} -> ${
          idx + 1
        }${isAbstract ? " (abstract: prompt match skipped)" : ""}`
      );
      return idx + 1;
    }

    console.log(
      `${logPrefix}> Strict caching index NOT synced for idx=${idx}: ` +
        `${promptMatches ? "" : "promptMismatch "}` +
        `${wasntUsed ? "" : "wasUsed "}` +
        `${commandsMatch ? "" : "cmdMismatch "}` +
        `${skipOk ? "" : "skipNotAllowed "}` +
        `${isAbstract ? "(abstract)" : ""}`,
      {
        baselinePrompt: step.prompt ?? "",
        aiStepPrompt: aiPrompt ?? "",
        baselineCommand: cachedFirstLine,
        aiStepCommand: aiFirstLine,
        skipGap: {
          skippedTaps: skipBreakdown.skippedTaps,
          skippedImportant: skipBreakdown.skippedImportant,
          intermediates: skipBreakdown.classifications,
        },
      }
    );
  }

  return currentCachedStepIndex;
};

// Mirrors `_minimized_baseline` in client_session.py on the FastAPI side: each
// baseline step is reduced to its sync index, the natural-language summary of
// what happened, and the per-line gpt commands. The caching-sync model uses
// this payload to decide which baseline step its current response corresponds
// to and returns the matching index in `cachingSyncIndex`.
const buildMinimizedCachingBaselineForSync = (
  cachedScreens: CachedScreenData[]
): Array<{
  caching_sync_index: number;
  actions_description: string;
  commands: string[];
}> => {
  return cachedScreens.map((step, i) => ({
    caching_sync_index: i,
    actions_description: step.actionsDescription ?? "",
    commands: (step.gptCommand ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0),
  }));
};

// Walks the prompt template, picks up every {{key}} token, and resolves
// each key against the run's env vars and input values. The resulting map
// maps `key` -> rendered string used during this run, and is later used
// to convert rendered gpt/appetize commands back into templated form for
// caching. Volatile placeholders (timestamp, randomNumberInRange, etc.)
// can't be resolved from envVars/inputValues alone - prefer
// extractDynamicValuesByAligning when a rendered prompt is available so
// those values are captured too.
const buildDynamicValuesForPrompt = ({
  promptTemplate,
  envVars,
  runInputValues,
}: {
  promptTemplate: string;
  envVars: EnvVar[];
  runInputValues: InputValue[];
}): Record<string, string> => {
  const map: Record<string, string> = {};
  const regex = /{{(.*?)}}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(promptTemplate)) !== null) {
    const key = match[1].trim();
    if (key.length === 0 || key in map) continue;

    let value: string | undefined;
    if (key.startsWith("env.")) {
      const envKey = key.slice("env.".length);
      const envVar = envVars.find((v) => v.key === envKey);
      value = envVar?.value;
    } else {
      value = runInputValues.find((v) => v.key === key)?.value;
    }

    if (value != null && value.length > 0) {
      map[key] = value;
    }
  }
  return map;
};

// Recovers the rendered value of every {{key}} placeholder in a template by
// aligning the template against its rendered counterpart. This lets us pick
// up volatile values like {{timestamp}} or {{randomNumberInRange(...)}} that
// can't be looked up statically, so they can be templatized back to
// {{key}} in cached commands and re-resolved fresh on the next run.
const extractDynamicValuesByAligning = (
  template: string,
  rendered: string
): Record<string, string> => {
  const map: Record<string, string> = {};
  if (!template || rendered == null) return map;

  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const orderedKeys: string[] = [];
  const regexParts: string[] = [];
  const tokenRegex = /{{(.*?)}}/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  while ((match = tokenRegex.exec(template)) !== null) {
    regexParts.push(escapeRegex(template.slice(lastIdx, match.index)));
    regexParts.push("([\\s\\S]*?)");
    orderedKeys.push(match[1].trim());
    lastIdx = match.index + match[0].length;
  }
  if (orderedKeys.length === 0) return map;
  regexParts.push(escapeRegex(template.slice(lastIdx)));

  let aligned: RegExpExecArray | null;
  try {
    aligned = new RegExp("^" + regexParts.join("") + "$").exec(rendered);
  } catch {
    return map;
  }
  if (aligned == null) return map;

  for (let i = 0; i < orderedKeys.length; i++) {
    const key = orderedKeys[i];
    const value = aligned[i + 1];
    if (value == null || value.length === 0) continue;
    if (map[key] != null && map[key] !== value) continue;
    map[key] = value;
  }
  return map;
};

// Replaces every literal occurrence of a rendered dynamic value with its
// {{key}} placeholder, longest value first to avoid partial overlaps when
// one value is a substring of another. Mirrors the encode side of
// replace_dynamic_values_in_commands in caching_utils.py.
const templatizeWithDynamicValues = (
  rendered: string,
  dynamicValues: Record<string, string>
): string => {
  if (!rendered) return rendered;
  const entries = Object.entries(dynamicValues)
    .filter(([, value]) => value != null && value.length > 0)
    .sort((a, b) => b[1].length - a[1].length);
  let out = rendered;
  for (const [key, value] of entries) {
    out = out.split(value).join(`{{${key}}}`);
  }
  return out;
};

const prepareCachedFlow = async (
  promptSteps: PromptStep[],
  cachedSteps: CachedScreenData[],
  cachingEnabled: boolean | undefined,
  platform: string = "android",
  organizationId: string,
  isPartialExecution: boolean = false
) => {
  if (cachingEnabled === undefined || !cachingEnabled) {
    console.log("> Caching disabled for this run");
    return {
      allowCachedRun: false,
      cachedData: [],
    };
  }

  const filteredPromptSteps = promptSteps
    .map((promptStep) => promptStep.text.replaceAll("\n", ""))
    .filter((text) => text !== "");

  let filteredCachedSteps: CachedScreenData[] = cachedSteps.filter(
    (value) => !value.appetizeCommand.includes("task complete:")
  );
  const wrongIndices: number[] = [];

  for (let i = 0; i < filteredCachedSteps.length; i++) {
    const cachedStep = filteredCachedSteps[i];
    cachedStep.baseIndex = i;
    let found = false;

    if (!isUnusableCachedScreen(cachedStep)) {
      // Try to find matching step only for usable cached screens
      for (const promptText of filteredPromptSteps) {
        if (
          promptsEquivalentIgnoringDynamic(
            cachedStep.prompt ?? "",
            promptText
          )
        ) {
          found = true;
          break;
        }
      }
    }

    if (!found) {
      wrongIndices.push(i);
    }
  }

  if (wrongIndices.length > 0) {
    filteredCachedSteps = filteredCachedSteps.filter(
      (_, index) => !wrongIndices.includes(index)
    );
  }
  console.log({ promptSteps, cachedSteps, filteredCachedSteps });
  return {
    allowCachedRun: cachingEnabled && filteredCachedSteps.length > 0,
    cachedData: filteredCachedSteps,
  };
};

const callFilteredCachedScreensAPI = async (
  wrongIndices: number[],
  cachedSteps: CachedScreenData[],
  platform: string,
  organizationId: string,
  isPartialExecution: boolean
) => {
  console.log("Calling filteredCachedScreens API with data: ", {
    wrongIndices,
    cachedSteps,
    platform,
    isPartialExecution,
    organizationId,
  });

  const FILTER_CACHED_SCREENS_URL = `${FASTAPI_SERVER_URL}/filterCachedScreens`;

  const requestObject = {
    wrongIndices,
    cachedSteps,
    isPartialExecution,
    organizationId,
    pixelsToCrop: {
      top: platform === "android" ? 60 : 80,
      bottom: 30,
      left: 5,
      right: 5,
    },
  };

  try {
    const jsonResponse = await fetchPlus(
      FILTER_CACHED_SCREENS_URL,
      {
        method: "POST",
        data: requestObject,
        timeout: 60000,
      },
      1
    );

    return jsonResponse as CachedScreenData[];
  } catch (error) {
    trackSentryException(error);
    console.error("Error calling filteredCachedScreens API:", error);
    return cachedSteps.filter((_, index) => !wrongIndices.includes(index));
  }
};

const callDeleteUploadAPI = async (uploadId: string) => {
  console.log("Calling deleteUpload API with uploadId: ", uploadId);

  const DELETE_UPLOAD_URL = `${FASTAPI_SERVER_URL}/${uploadId}`;

  return await fetchPlus(
    DELETE_UPLOAD_URL,
    {
      method: "DELETE",
      timeout: 15000,
    },
    1
  );
};

const callGetCachedScreenAPI = async (
  currentScreenshot: string,
  currentlyUsedCachedScreens: CachedScreenData[],
  pixelsToCrop: PixelsToCrop,
  organizationId: string,
  aggressiveCachingEnabled: boolean
) => {
  if (currentlyUsedCachedScreens.length === 0) {
    console.log(
      "No currently used cached screens, no need to call getCachedScreen API"
    );
    return [];
  }
  console.log("Calling getCachedScreen API with data: ", {
    currentScreenshot,
    currentlyUsedCachedScreens,
    pixelsToCrop,
    organizationId,
    aggressiveCachingEnabled,
  });

  const GET_CACHED_SCREENSHOT_URL = `${FASTAPI_SERVER_URL}/getCachedScreenshot`;

  const requestObject = {
    currentScreenshot,
    currentlyUsedCachedScreens: currentlyUsedCachedScreens.map(
      (value) => value.screenshotUrl
    ),
    currentAppetizeCommands: currentlyUsedCachedScreens.map(
      (value) => value.appetizeCommand
    ),
    pixelsToCrop,
    organizationId,
    aggressiveCachingEnabled,
  };

  try {
    const jsonResponse = await fetchPlus(
      GET_CACHED_SCREENSHOT_URL,
      {
        method: "POST",
        data: requestObject,
        timeout: 15000,
      },
      1
    );

    return jsonResponse as GetCachedScreenAPIResults[];
  } catch (error) {
    trackSentryException(error);
    console.error("Error calling getCachedScreen API:", error);
    return [];
  }
};

const callCancelSuiteAPI = async (
  cancelSingleRun: boolean,
  testSuiteId: string | undefined,
  runId: string | undefined = undefined
) => {
  console.log("Calling cancelSuiteAPI with data: ", {
    testSuiteId,
    runId,
    cancelSingleRun,
  });

  const endpoint = cancelSingleRun ? "cancelSingleRun" : "cancelSuite";
  const CANCEL_ENDPOINT_URL = `${BACKEND_SERVER_URL}/${endpoint}`;

  const requestObject = {
    testSuiteId,
    runId,
  };

  try {
    await fetchPlus(
      CANCEL_ENDPOINT_URL,
      {
        method: "POST",
        data: requestObject,
        timeout: 30000,
      },
      1
    );
  } catch (error) {
    trackSentryException(error);
    console.error("Error calling callCancelSuiteAPI API:", error);
    throw error;
  }
};

async function deleteFirestoreFolder(
  path: string,
  fileNameStartsWith?: string
) {
  const folderRef = ref(customStorage, path);
  const fileList = await listAll(folderRef);
  const promises = fileList.items
    .filter((item) => {
      if (fileNameStartsWith == null) {
        return true;
      }
      return item.name.startsWith(fileNameStartsWith);
    })
    .map(async (item) => {
      await deleteObject(item);
    });

  return await Promise.all(promises);
}

const retryOperation = async <T>(
  operation: (executedTimes: number) => Promise<T> | T,
  delay: number,
  executeTimes: number,
  executedTimes?: number
): Promise<T> => {
  try {
    return await operation(executedTimes ?? 0);
  } catch (ex) {
    trackSentryException(ex);
    if (ex?.cancelRetry) {
      // We already cancelled the retry once, set the flag to false so that outer retryOperation function can retry
      // Needed for appetize clone session creation
      throw { ...ex, cancelRetry: false };
    } else {
      if (executeTimes > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return await retryOperation(
          operation,
          delay,
          executeTimes - 1,
          (executedTimes ?? 0) + 1
        );
      } else {
        throw ex;
      }
    }
  }
};

const getInputValues = ({
  currentRun,
  nextRunsToExecute = [],
  envVars,
}: {
  currentRun: RunExecutionData;
  nextRunsToExecute?: RunExecutionData[];
  envVars: EnvVar[];
}): InputValue[] => {
  if (!currentRun.settings.inputKeys) {
    return [];
  }

  return currentRun.settings.inputKeys.map<InputValue>(
    (inputKeyFromSettings) => {
      // Find input value and relevant run
      const { inputValue, relevantRun } = findInputValueForKey(
        currentRun,
        nextRunsToExecute,
        inputKeyFromSettings.key
      );

      // Determine raw value with fallbacks
      const rawValue = getValueWithFallbacks(
        inputValue,
        inputKeyFromSettings.value,
        inputKeyFromSettings.defaultValue
      );

      // Process value
      const processedValue = processInputValue(
        rawValue,
        inputKeyFromSettings.key,
        relevantRun,
        envVars
      );

      return {
        key: inputKeyFromSettings.key,
        value: processedValue,
      };
    }
  );
};

function findInputValueForKey(
  currentRun: RunExecutionData,
  nextRunsToExecute: RunExecutionData[] = [],
  inputKey: string
) {
  let inputValue: InputValue | undefined;
  let relevantRun: RunExecutionData | undefined;

  // No need to search if currentRun has no testId
  if (!currentRun.testId) {
    return { inputValue, relevantRun };
  }

  // First check in parent run (last run)
  const parentRun = nextRunsToExecute.at(-1);
  const resultFromParent = findValueInRun(
    currentRun.testId,
    inputKey,
    parentRun
  );

  if (resultFromParent) {
    return resultFromParent;
  }

  // Then check in other runs
  for (const run of nextRunsToExecute.slice(0, -1)) {
    const result = findValueInRun(currentRun.testId, inputKey, run);
    if (result) {
      return result;
    }
  }

  return { inputValue, relevantRun };
}

function processInputValue(
  value: string,
  inputKey: string,
  run: RunExecutionData | undefined,
  envVars: EnvVar[]
): string {
  // Process escaped newlines
  const processedValue = value.replace(/\\n/g, "\n");

  // Process environment variables
  return processedValue.replace(
    /{{(.*?)}}/g,
    (match, key) =>
      replaceEnvVars({
        item: key.trim(),
        envVars,
        revealSecrets: true,
        executionData: {
          testName: run ? run.title ?? run.runData?.title ?? "" : "",
          testInputKey: inputKey,
        },
      }) ?? ""
  );
}

function getValueWithFallbacks(
  inputValue: InputValue | undefined,
  primaryFallback?: string,
  secondaryFallback?: string
): string {
  // Check if inputValue exists and has a non-empty value
  if (inputValue?.value != null && inputValue.value.trim() !== "") {
    return inputValue.value;
  }

  // Otherwise use fallback chain
  return primaryFallback ?? secondaryFallback ?? "";
}

function findValueInRun(
  testId: string,
  inputKey: string,
  run?: RunExecutionData
): { inputValue?: InputValue; relevantRun?: RunExecutionData } | undefined {
  if (!run) {
    return undefined;
  }

  const findInValues = (values: InputValue[]) => {
    const found = values.find((v) => v.key === inputKey);
    return found?.value != null && found.value.trim() !== ""
      ? { inputValue: found, relevantRun: run }
      : undefined;
  };

  return (
    findInValues(run.dependencyInputValues?.global ?? []) ??
    findInValues(run.dependencyInputValues?.[testId] ?? [])
  );
}

const shortenCommands = (
  command: string,
  promptSteps: PromptStep[],
  lastStepNumber: number,
  fromTop: boolean = false
) => {
  const startIndex = promptSteps.findIndex((promptStep) =>
    promptStep.text.startsWith(`${lastStepNumber}.`)
  );

  const CUTTING_FROM_TOP_LIMIT = 6;

  if (fromTop) {
    return {
      shortenedCommands: getPromptStepsText(
        promptSteps.slice(0, CUTTING_FROM_TOP_LIMIT)
      ),
      shortenedPromptSteps: promptSteps.slice(0, CUTTING_FROM_TOP_LIMIT),
    };
  }

  if (startIndex !== -1) {
    return {
      shortenedCommands: getPromptStepsText(promptSteps.slice(startIndex)),
      shortenedPromptSteps: promptSteps.slice(startIndex),
    };
  }
  return {
    shortenedCommands: command,
    shortenedPromptSteps: promptSteps,
  };
};

const getExecutionData = async (requestObject: any) => {
  let response;

  try {
    response = await fetchPlus(
      LAMBDA_AWS_URL,
      {
        method: "POST",
        data: requestObject,
        timeout: LAMBDA_AWS_TIMEOUT,
      },
      0
    );
  } catch (e) {
    trackSentryException(e);
    // Same default as a new organisation's model_version.fallback in the backend
    const fallbackModel: string =
      requestObject?.fallbackModel ?? "claude-agent-testing";

    console.log(
      `Falling back from model ${requestObject.model_version} to ${fallbackModel}`
    );
    requestObject.model_version = fallbackModel;

    response = await fetchPlus(
      LAMBDA_AWS_URL,
      {
        method: "POST",
        data: requestObject,
        timeout: LAMBDA_AWS_TIMEOUT,
      },
      1,
      {
        500: "Sorry, something went wrong during execution. Please try again.",
      }
    );
  }
  return response;
};

const calculateMinutes = (x: number): number => {
  const secondsPerX = 20; // Define how many seconds one x represents
  const totalSeconds = x * secondsPerX; // Calculate total seconds for given x
  const minutes = Math.round(totalSeconds / 60); // Convert total seconds to minutes

  return minutes > 0 ? minutes : 1; // Round the result to the nearest whole number
};

const capitalize = (s: string) => {
  return (s !== "" && s[0].toUpperCase() + s.slice(1)) || "";
};

const findDuolingoRequest = (logs: NetworkLog[]) => {
  const responsesReversed = logs
    .reverse()
    .filter((log: any) => log.type === "response");

  const foundStory = responsesReversed.find((response) =>
    response.request.url.includes("https://stories.duolingo.com/api2/stories/")
  );

  const foundTest = responsesReversed.find((response) =>
    response.request.url.includes(
      "https://android-api-cf.duolingo.com/2017-06-30/sessions"
    )
  );

  if (foundStory != null) {
    const content = JSON.parse(foundStory.response.content.text);
    const challenges = content?.elements ?? [];

    return { challenges, type: "story" };
  } else if (foundTest != null) {
    const content = JSON.parse(foundTest.response.content.text);
    const challenges = content?.challenges ?? [];

    return { challenges, type: "test" };
  }
  return undefined;
};

const extractDuolingoAnswerForTest = (challenge: any) => {
  const type = challenge.type as string;

  switch (type) {
    case "select":
      return {
        prompt: challenge.metadata.phrase,
        correct_answer: [challenge.choices[challenge.correctIndex].hint],
        type,
        title: "Select the correct image",
      };
    case "translate":
      return {
        prompt: challenge.prompt,
        correct_answer: challenge.correctTokens,
        type,
        title: "Translate this sentence",
      };
    case "listenTap":
      return {
        prompt: challenge.prompt,
        correct_answer: (challenge.solutionTranslation as string)
          .replace(".", "")
          .split(" "),
        type,
        title: "Translate this sentence",
      };
    case "tapComplete":
      return {
        prompt: (
          challenge.displayTokens.filter(
            (token: any) => !token.isBlank
          ) as string[]
        )
          .map((token: any) => token.text)
          .join(""),
        correct_answer: (challenge.correctIndices as number[]).map(
          (index) => challenge.choices[index].text
        ),
        type,
        title: "Complete the sentence",
      };
    case "assist":
      return {
        prompt: challenge.prompt,
        correct_answer: challenge.choices[challenge.correctIndex],
        type,
        title: "Select the correct translation",
      };
    case "match":
      return {
        prompt: "Tap the matching pairs",
        correct_answer: Object.entries(
          challenge.metadata.pairs.map((pair: any) => [
            pair.learning_word,
            pair.translation,
          ])
        ).flat(),
        type,
        title: "Tap the matching pairs",
      };
    default:
      console.log(`Unknown Duolingo challenge type: ${type}`);
      return undefined;
  }
};

const extractDuolingoAnswerForStory = (challenge: any) => {
  const type = challenge.type as string;

  switch (type) {
    case "SELECT_PHRASE":
      return {
        prompt: "Select the missing phrase",
        correct_answer: challenge.answers[challenge.correctAnswerIndex],
        type,
      };
    case "POINT_TO_PHRASE":
      // eslint-disable-next-line no-case-declarations
      const selectableAnswers = challenge.transcriptParts
        .filter((answer: any) => answer.selectable === true)
        .map((answer: any) => answer.text);

      return {
        prompt: challenge.question.text,
        correct_answer: selectableAnswers[challenge.correctAnswerIndex],
        type,
      };
    case "MULTIPLE_CHOICE":
      return {
        prompt: challenge?.question?.text ?? "What comes next?",
        correct_answer: challenge.answers[challenge.correctAnswerIndex].text,
        type,
      };
    case "ARRANGE":
      // eslint-disable-next-line no-case-declarations
      const correctIndices: number[] = challenge.phraseOrder;
      return {
        prompt: "Tap what you hear",
        correct_answer: correctIndices.map(
          (index: number) => challenge.selectablePhrases[index]
        ),
        type,
      };
    case "MATCH":
      return {
        prompt: "Tap the pairs",
        correct_answer: challenge.matches.map((pair: any) => [
          pair.translation,
          pair.phrase,
        ]),
        type,
      };
    default:
      console.log(`Unknown Duolingo challenge type: ${type}`);
      return undefined;
  }
};

const prepareDuolingoAnswerSheet = (
  challengesList: any[],
  quizType: string
) => {
  console.log("Preparing answer sheet for challenges list", { challengesList });
  const extractAnswer = (challenge: any) => {
    try {
      if (quizType === "test") {
        return extractDuolingoAnswerForTest(challenge);
      }
      if (quizType === "story") {
        return extractDuolingoAnswerForStory(challenge);
      }
    } catch (e) {
      console.error("Error extracting answer from challenge", e);
      return undefined;
    }
  };

  const challenges = challengesList.map((challenge: any) =>
    extractAnswer(challenge)
  );

  return challenges.filter((challenge: any) => challenge !== undefined);
};

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return "th"; // Covers 11th to 19th
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const getFormattedDate = (date: Date) => {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  const ordinalSuffix = getOrdinalSuffix(day);

  return `${day}${ordinalSuffix} ${month} ${year}`;
};

const getISO8601Date = ({
  dateTime,
  offset = 0,
}: {
  dateTime: DateTime;
  offset?: number;
}): string => {
  const adjustedDate = offset ? dateTime.plus({ hours: offset }) : dateTime;
  return adjustedDate.toUTC().toString();
};

function findElementAtCoordinatesIOS(xmlDoc, x, y): Element {
  const stack = Array.from(xmlDoc.documentElement.children); // Start with all children of the root element
  let bestMatch = null;

  while (stack.length > 0) {
    const node = stack.pop();

    // Check visibility; assume true if not explicitly false
    const isVisible = node.getAttribute("visible") !== "false"; // Treat null or missing as visible

    if (!isVisible) continue; // Skip elements that are explicitly marked as not visible

    const xAttr = parseFloat(node.getAttribute("x"));
    const yAttr = parseFloat(node.getAttribute("y"));
    const widthAttr = parseFloat(node.getAttribute("width"));
    const heightAttr = parseFloat(node.getAttribute("height"));

    if (
      !isNaN(xAttr) &&
      !isNaN(yAttr) &&
      !isNaN(widthAttr) &&
      !isNaN(heightAttr)
    ) {
      if (
        x >= xAttr &&
        x <= xAttr + widthAttr &&
        y >= yAttr &&
        y <= yAttr + heightAttr
      ) {
        // Update the bestMatch to this node if it's more specific (smaller bounding box)
        if (
          bestMatch === null ||
          widthAttr * heightAttr <
            parseFloat(bestMatch.getAttribute("width")) *
              parseFloat(bestMatch.getAttribute("height"))
        ) {
          bestMatch = node;
        }
      }
    }

    // Push children onto the stack to check if any are a better match (deeper in hierarchy)
    const children = Array.from(node.children);
    for (const child of children) {
      stack.push(child);
    }
  }

  return bestMatch; // Return the most specific (smallest) element that matches the coordinates
}

function parseBounds(boundsStr) {
  const match = boundsStr.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
  if (match) {
    return {
      left: parseInt(match[1], 10),
      top: parseInt(match[2], 10),
      right: parseInt(match[3], 10),
      bottom: parseInt(match[4], 10),
    };
  }
  return null;
}

function findElementAtCoordinatesAndroid(xmlDoc, x, y): Element {
  const stack = Array.from(xmlDoc.documentElement.children); // Start with all children of the root element
  let bestMatch = null;

  while (stack.length > 0) {
    const node = stack.pop();

    const boundsStr = node.getAttribute("bounds");
    const bounds = parseBounds(boundsStr);

    if (bounds != null) {
      if (
        x >= bounds.left &&
        x <= bounds.right &&
        y >= bounds.top &&
        y <= bounds.bottom
      ) {
        // Update the bestMatch to this node if it's more specific (smaller bounding box)
        const area =
          (bounds.right - bounds.left) * (bounds.bottom - bounds.top);
        if (
          bestMatch === null ||
          area <
            (bestMatch.right - bestMatch.left) *
              (bestMatch.bottom - bestMatch.top)
        ) {
          bestMatch = {
            node,
            left: bounds.left,
            top: bounds.top,
            right: bounds.right,
            bottom: bounds.bottom,
          };
        }
      }
    }

    // Push children onto the stack to check if any are a better match (deeper in hierarchy)
    const children = Array.from(node.children);
    for (const child of children) {
      stack.push(child);
    }
  }

  return bestMatch != null ? bestMatch.node : null; // Return the most specific (smallest) element that matches the coordinates
}

const generateSwipeOrScrollAppiumCode = ({
  startX,
  startY,
  endX,
  endY,
  duration,
  deviceHeight,
  deviceWidth,
}: {
  startX: number;
  endX: number;
  startY: number;
  endY: number;
  duration: number;
  deviceHeight: number;
  deviceWidth: number;
}): string => {
  let naturalLanguagePrompt;

  const { direction, percentage } = getSlideDirectionAndPercentageFromAssistant(
    {
      startX,
      startY,
      endX,
      endY,
      rectHeight: deviceHeight,
      rectWidth: deviceWidth,
    }
  );
  const directionInverted = invertDirection(direction);

  if (direction == "up" || direction == "down") {
    naturalLanguagePrompt = `scroll ${directionInverted}`;
  } else {
    if (
      startY < 0.2 * deviceHeight ||
      startY > 0.8 * deviceHeight ||
      percentage < 20
    ) {
      naturalLanguagePrompt = `slide ${direction} ${percentage}% ; at x=${startX} y=${startY}`;
    } else {
      naturalLanguagePrompt = `swipe ${direction}`;
    }
  }

  const generatedCode = `const actions = [
       {
         type: 'pointer',
         id: 'finger1',
         parameters: { pointerType: 'touch' },
         actions: [
          { type: 'pointerMove', duration: 0, x: ${startX}, y: ${startY} },
          { type: 'pointerDown', button: 0 },
          { type: 'pointerMove', duration: ${duration}, x: ${endX}, y: ${endY} },
          { type: 'pointerUp', button: 0 },
         ],
       },
      ];

      await driver.performActions(actions);
      console.log('Swipe ${direction} by ${percentage.toFixed(2)}%');`;

  return `await gptDriver.execute("${naturalLanguagePrompt}",
   async (driver: WebdriverIO.Browser) => {
      ${generatedCode}
   });
`;
};

function generateTapAppiumCodeForAndroid(clickedElement): string {
  let naturalLanguagePrompt;
  let generatedCode;

  // Check for accessibility id first
  const accessibilityId = clickedElement.getAttribute("content-desc");
  if (accessibilityId) {
    generatedCode = `const element = await driver.$('~${accessibilityId}');
      await element.click();`;
    naturalLanguagePrompt = `tap on element with accessibility id ${accessibilityId}`;
  } else {
    // Generate XPath using essential attributes
    const resourceId = clickedElement.getAttribute("resource-id");
    const text = clickedElement.getAttribute("text");
    const className = clickedElement.getAttribute("class");

    const conditions = [];

    if (resourceId) conditions.push(`@resource-id='${resourceId}'`);
    if (text) conditions.push(`@text='${text}'`);
    if (className) conditions.push(`@class='${className}'`);

    // Ensure there are conditions before using xpath
    if (conditions.length > 0) {
      const xpath = `//*[${conditions.join(" and ")}]`;
      generatedCode = `const element = await driver.$("${xpath}");
      await element.click();`;
      naturalLanguagePrompt =
        text != null ? `tap on ${text}` : `tap on element with XPath ${xpath}`;
    } else {
      console.error("No valid attributes found to generate XPath.");
      return; // Exit if no valid attributes are available
    }
  }

  return `await gptDriver.execute("${naturalLanguagePrompt}",
   async (driver: WebdriverIO.Browser) => {
      ${generatedCode}
   });
`;
}

function parseXml(xmlStr) {
  return new window.DOMParser().parseFromString(xmlStr, "text/xml");
}

function generateTapAppiumCodeForiOS(clickedElement): string {
  let naturalLanguagePrompt;
  let generatedCode;

  // Generate a more specific XPath using the correct attributes
  const type = clickedElement.getAttribute("type");
  const label = clickedElement.getAttribute("label");
  const name = clickedElement.getAttribute("name");

  let xpath;

  // Check for parent uniqueness to narrow down the element selection
  if (type && label && name) {
    xpath = `//${type}[@name='${name}' and @label='${label}']`;
  } else {
    console.error("Not enough attributes to generate a specific XPath.");
    return; // Exit if not enough attributes are available
  }

  generatedCode = `const element = await driver.$("${xpath}");
      await element.click();`;
  naturalLanguagePrompt =
    name != null ? `tap on ${name}` : `tap on element with XPath ${xpath}`;

  return `await gptDriver.execute("${naturalLanguagePrompt}",
   async (driver: WebdriverIO.Browser) => {
      ${generatedCode}
   });
`;
}

/**
 * Determines if the polling should retry on the encountered error.
 *
 * @param error - The error encountered during the polling request.
 * @returns `true` if the error should trigger a retry, `false` otherwise.
 */
function shouldRetry(error: any): boolean {
  // Retry on 5xx server errors. Adjust the condition based on your needs.
  return error.response && error.response.status >= 500;
}

/**
 * Polls a given URL until a specified condition is met.
 *
 * @param {string} url The URL to poll.
 * @param {function} condition A function that takes the response and decides whether to stop polling.
 * @param {number} interval The interval in seconds between poll attempts.
 * @param {number} maxAttempts The maximum number of attempts to make.
 */
async function poll<T>(
  url: string,
  condition: (response: AxiosResponse<T>) => boolean,
  interval: number,
  maxAttempts: number,
  axiosInstance: AxiosInstance | undefined
): Promise<AxiosResponse<T>> {
  let attempts = 0;

  const executePoll = async (
    resolve: (value: AxiosResponse<T>) => void,
    reject: (reason?: any) => void
  ) => {
    try {
      const response = await (axiosInstance ?? axios).get<T>(url);
      attempts++;

      if (condition(response)) {
        resolve(response);
      } else if (attempts >= maxAttempts) {
        reject(
          new Error(
            "Maximum attempts reached without fulfilling the condition."
          )
        );
      } else {
        setTimeout(async () => {
          await executePoll(resolve, reject);
        }, interval * 1000);
      }
    } catch (error) {
      console.log("error", error);
      if (shouldRetry(error)) {
        if (attempts >= maxAttempts) {
          reject(new Error("Maximum attempts reached with server errors."));
        } else {
          console.log("Retrying due to server error...");
          setTimeout(async () => {
            await executePoll(resolve, reject);
          }, interval * 1000);
        }
      } else {
        console.log("Error encountered during polling:", error);
        reject(error);
      }
    }
  };

  return await new Promise(executePoll);
}

const determineModelVersion = (
  nextAndFuturePromptStepsList: Array<undefined | string>,
  fromEditor: boolean,
  companyData: CompanyData
) => {
  const useVisionModel = nextAndFuturePromptStepsList.some(
    (stepText) =>
      stepText?.includes("withVision:") ||
      (stepText?.includes("assertVisible") &&
        companyData.settings.enableAssertVisibleAIFallback)
  );

  const useClaudeVisionAgentForScroll = nextAndFuturePromptStepsList.some(
    (stepText) =>
      stepText?.includes("scroll") ||
      stepText?.includes("swipe") ||
      stepText?.includes("slide")
  );

  if (useVisionModel) {
    return useClaudeVisionAgentForScroll
      ? "claude-agent-4"
      : companyData.visionModel;
  }

  return fromEditor
    ? companyData.editorExecutionModel
    : companyData.serverExecutionModel;
};

const isLocalCommand = (command: string): boolean => {
  command = command.trim();

  const appetizeCommands = [
    "tapOn:",
    "tapOn.id:",
    "longPressOn:",
    "longPressOn.id:",
    "zoom:",
    "type:",
    "press:",
    "launchApp:",
    "openLink:",
    "openAppClipLink:",
    "restartApp",
    "matchBiometry:",
    "scrollUpUntilVisible.text:",
    "scrollUpUntilVisible.id:",
    "scrollDownUntilVisible.text:",
    "scrollDownUntilVisible.id:",
    "scrollToBottom",
    "waitUntilVisible.text:",
    "waitUntilVisible.id:",
    "swipe:",
    "scroll:",
    "slide:",
    "assertVisible.text:",
    "assertNotVisible.text:",
    "assertVisible.id:",
    "assertNotVisible.id:",
    "pressBackButton",
    "wait:",
    "removeText:",
    "go to:",
    "offlineMode:",
    "curlRequest:",
    "takeScreenshot",
    "openInfoBar",
    "closeInfoBar",
    "play:",
    "playVoice:",
    "listen",
    "extractLinkFromEmail:",
    "multiTap.id:",
    "multiTap.text:",
  ];

  return appetizeCommands.some((appetizeCommand) =>
    command.toLowerCase().startsWith(appetizeCommand.toLowerCase())
  );
};

const parseLocalAppetizeCommand = (
  promptStep: PromptStep | undefined
): string => {
  const plainText = promptStep?.plainText?.replace("tapOn", "tabOn");

  if (!plainText) {
    return "";
  }

  const acceptedCommands = [
    "assertVisible.id:",
    "assertNotVisible.id:",
    "assertVisible.text:",
    "assertNotVisible.text:",
    "scrollUpUntilVisible.id:",
    "scrollDownUntilVisible.id:",
    "scrollUpUntilVisible.text:",
    "scrollDownUntilVisible.text:",
    "scrollToBottom",
  ];

  const lowerText = plainText.toLowerCase();
  const matchedCommand = acceptedCommands.find((cmd) =>
    lowerText.startsWith(cmd.toLowerCase())
  );

  if (!matchedCommand) {
    return plainText; // Unrecognized command, return as-is
  }

  // Extract value after the matched command prefix
  const valueAfterCommand = plainText.substring(matchedCommand.length).trim();

  return `${matchedCommand}${valueAfterCommand} Result=true`;
};

interface JsonNode {
  attributes?: {
    "resource-id"?: string;
    text?: string;
    accessibilityIdentifier?: string;
    "content-desc"?: string;
    visible?: boolean;
  };
  children?: JsonNode[];
  bounds: { x: number; y: number; width: number; height: number };

  [key: string]: any;
}

interface SearchConfig {
  targetId?: string;
  text?: string;
  isAssertion?: boolean;
}

const compareDrawingOrder = (a: TreeNode, b: TreeNode): number => {
  if (!a.bounds && !b.bounds) return 0;
  if (!a.bounds) return -1;
  if (!b.bounds) return 1;

  const aPath = a.drawingOrderPath ?? a.indexPath ?? [];
  const bPath = b.drawingOrderPath ?? b.indexPath ?? [];

  // Lexicographic comparison of path arrays (descending - higher values first)
  for (let i = 0; i < Math.min(aPath.length, bPath.length); i++) {
    const aVal = parseInt(aPath[i]);
    const bVal = parseInt(bPath[i]);
    if (aVal !== bVal) {
      return aVal - bVal; // Higher drawing order = drawn on top
    }
  }

  // Longer path = more nested = drawn on top
  if (aPath.length !== bPath.length) {
    return aPath.length - bPath.length;
  }

  // Fallback: smaller elements on top
  return b.bounds.width * b.bounds.height - a.bounds.width * a.bounds.height;
};

const findShallowestRelatedNode = (nodes: TreeNode[]): TreeNode | null => {
  if (nodes.length <= 1) {
    return null;
  }

  const firstNode = nodes[0];
  const firstNodePath = firstNode.drawingOrderPath ?? firstNode.indexPath ?? [];

  // Find nodes that are ancestors or descendants of the first node
  const relatedNodes = nodes.filter((node) => {
    const nodePath = node.drawingOrderPath ?? node.indexPath ?? [];
    const minLen = Math.min(firstNodePath.length, nodePath.length);

    // Check if one path is a prefix of the other (ancestor/descendant relationship)
    const isRelated = firstNodePath
      .slice(0, minLen)
      .every((val, idx) => val === nodePath[idx]);
    return isRelated;
  });

  if (relatedNodes.length <= 1) {
    return null;
  }

  // Pick the shallowest (least nested) node
  relatedNodes.sort((a, b) => {
    const aPath = a.drawingOrderPath ?? a.indexPath ?? [];
    const bPath = b.drawingOrderPath ?? b.indexPath ?? [];
    return aPath.length - bPath.length;
  });

  return relatedNodes[0];
};

const isDescendant = ({
  potentialChild,
  potentialParent,
}: {
  potentialChild: TreeNode;
  potentialParent: TreeNode;
}): boolean => {
  const childPath =
    potentialChild.drawingOrderPath ?? potentialChild.indexPath ?? [];
  const parentPath =
    potentialParent.drawingOrderPath ?? potentialParent.indexPath ?? [];

  // A descendant's path must be longer and start with the parent's path
  if (childPath.length <= parentPath.length) {
    return false;
  }

  // Check if all elements of parent path match the start of child path
  return parentPath.every((segment, index) => segment === childPath[index]);
};

const getNonOccludedElements = ({
  items,
  allItems,
}: {
  items: TreeNode[];
  allItems?: TreeNode[];
}) => {
  return items.filter((node) => {
    if (node.isFixed) {
      return true;
    }

    const occludingElement = getOccludingElement({
      targetElement: node,
      allElements: allItems ?? items,
      occlusionThreshold: 0.6,
    });

    return !occludingElement;
  });
};

const getOccludingElement = ({
  targetElement,
  allElements,
  occlusionThreshold = 1, // 1.0 = 100% area covered, 0.5 = 50% area covered
}: {
  targetElement: TreeNode;
  allElements: TreeNode[];
  occlusionThreshold?: number; // Range: 0.0 to 1.0
}): TreeNode | null => {
  const { x, y, width, height } = targetElement.bounds!;
  const targetArea = width * height;

  const occludingElement = allElements.find((otherElement) => {
    if (otherElement.bounds == null) {
      return false;
    }

    if (otherElement === targetElement) {
      return false;
    }

    if (
      isDescendant({
        potentialChild: targetElement,
        potentialParent: otherElement,
      }) ||
      isDescendant({
        potentialChild: otherElement,
        potentialParent: targetElement,
      })
    ) {
      return false;
    }

    // Check if this element has higher drawing order than target
    const hasHigherDrawingOrder =
      compareDrawingOrder(otherElement, targetElement) > 0 ||
      otherElement.isFixed;

    if (!hasHigherDrawingOrder) {
      return false;
    }

    // Calculate intersection dimensions
    const intersectX = Math.max(x, otherElement.bounds.x);
    const intersectY = Math.max(y, otherElement.bounds.y);
    const intersectRight = Math.min(
      x + width,
      otherElement.bounds.x + otherElement.bounds.width
    );
    const intersectBottom = Math.min(
      y + height,
      otherElement.bounds.y + otherElement.bounds.height
    );

    // Check if there's an intersection
    if (intersectRight <= intersectX || intersectBottom <= intersectY) {
      return false;
    }

    const intersectionWidth = intersectRight - intersectX;
    const intersectionHeight = intersectBottom - intersectY;
    const intersectionArea = intersectionWidth * intersectionHeight;

    const coverageRatio = intersectionArea / targetArea;

    const isOccluded = coverageRatio >= occlusionThreshold;

    // if (isOccluded) {
    //   console.log("coverageRatio", coverageRatio);
    // }

    return isOccluded;
  });

  return occludingElement ?? null;
};

interface TextCandidate {
  node: TreeNode;
  text: string;
  contentDesc: string;
}

/**
 * Searches for a node in the tree based on resource-id or text
 * @param data - The JsonNode or array of JsonNodes to search through
 * @param target - The target value or config object with search criteria
 * @param searchType - What to search for: 'resource-id', 'text', or 'both'
 * @param threshold - Maximum Fuse.js score for text matches (0-1, lower is better), default 0.3
 * @returns The matching node if returnNode is true, otherwise a boolean indicating if a match was found
 */
function findNode({
  data,
  searchType = "both",
  target,
}: {
  data: TreeNode[];
  target: SearchConfig;
  searchType?: "resource-id" | "text" | "both";
}): TreeNode[] {
  // Handle different target types
  const targetId = target.targetId;
  const targetText = target.text;

  // Return if no search criteria provided
  if (!targetId && !targetText) {
    return [];
  }

  // For text searches, use fuzzy matching with Fuse.js
  if (searchType === "text" && targetText) {
    const candidates: TextCandidate[] = [];
    collectTextCandidates(data, candidates);
    console.log("candidates", candidates);

    if (candidates.length === 0) {
      return [];
    }
    let targetTextLower = targetText?.toLowerCase()?.trim();
    if (targetTextLower) {
      targetTextLower = normalizeText(targetTextLower);
    }

    const results = candidates.filter((candidate) => {
      if (target.isAssertion) {
        return [
          candidate.text?.toLowerCase()?.includes(targetTextLower),
          candidate.contentDesc?.toLowerCase()?.includes(targetTextLower),
        ].includes(true);
      }
      return [
        candidate.text?.toLowerCase() === targetTextLower,
        candidate.contentDesc?.toLowerCase() === targetTextLower,
      ].includes(true);
    });

    console.log("results", results);

    if (results.length === 0) {
      return [];
    }

    return results.map((result) => result.node);
  }

  if (searchType === "both") {
    if (targetId) {
      const resourceIdResult = findByResourceId(data, targetId);
      if (resourceIdResult) {
        return resourceIdResult;
      }
    }

    if (targetText) {
      const candidates: TextCandidate[] = [];
      collectTextCandidates(data, candidates);
      console.log("candidates", candidates);

      if (candidates.length === 0) {
        return [];
      }

      let targetTextLower = targetText?.toLowerCase()?.trim();
      if (targetTextLower) {
        targetTextLower = normalizeText(targetTextLower);
      }

      const results = candidates.filter((candidate) => {
        if (target.isAssertion) {
          return [
            candidate.text?.toLowerCase()?.includes(targetTextLower),
            candidate.contentDesc?.toLowerCase()?.includes(targetTextLower),
          ].includes(true);
        }
        return [
          candidate.text?.toLowerCase() === targetTextLower,
          candidate.contentDesc?.toLowerCase() === targetTextLower,
        ].includes(true);
      });

      console.log("results", results);

      if (results.length === 0) {
        return [];
      }

      return results.map((result) => result.node);
    }

    return [];
  }

  if (searchType === "resource-id" && targetId) {
    return findByResourceId(data, targetId);
  }

  return [];
}

/**
 * Finds nodes by resource-id using exact matching
 */
function findByResourceId(data: TreeNode[], targetId: string): TreeNode[] {
  return data.filter((node) => {
    if (node.attributes != null) {
      return (
        node.attributes["resource-id"] === targetId ||
        node.attributes.accessibilityIdentifier === targetId
      );
    }
    return false;
  });
}

/**
 * Collects all visible text candidates from the tree
 */
function collectTextCandidates(
  data: TreeNode | TreeNode[],
  candidates: TextCandidate[]
): void {
  if (data == null) return;

  // If data is an array, recursively check each item
  if (Array.isArray(data)) {
    for (const item of data) {
      collectTextCandidates(item, candidates);
    }
    return;
  }

  // Check if attributes exist and node is visible
  if (data.attributes != null) {
    let text = data.attributes.text ?? "";
    let contentDesc = data.attributes["content-desc"] ?? "";

    if (text) text = normalizeText(text);
    if (contentDesc) contentDesc = normalizeText(contentDesc);

    // Only add candidates that have some text
    if (text || contentDesc) {
      candidates.push({
        node: data,
        text,
        contentDesc,
      });
    }
  }
}

function findNodeByConfig({
  data,
  config,
}: {
  data: TreeNode[];
  config: SearchConfig;
}): TreeNode[] {
  return findNode({ data, target: config, searchType: "both" });
}

function groupSteps(steps: PromptStep[]): PromptStep[][] {
  const result: PromptStep[][] = [];
  let currentGroup: PromptStep[] = [];

  for (const step of steps) {
    if (step.testStep !== undefined) {
      // If there's a current group, add it to the result
      if (currentGroup.length > 0) {
        result.push(currentGroup);
        currentGroup = [];
      }
      // Add the step with testStepId as its own group
      result.push([step]);
    } else {
      // Add the step to the current group if it has no testStepId
      if (step.plainText != null && step.plainText.trim().length > 0) {
        currentGroup.push(step);
      }
    }
  }

  // Add any remaining steps to the result
  if (currentGroup.length > 0) {
    result.push(currentGroup);
  }

  return result;
}

const parseTestEditorLogs = (logs: CachedScreenData[]) => {
  return logs.reduce<CachedScreenData[][]>((prev, current) => {
    const parsedCurrent = { ...current, title: current.title ?? "parent" };
    if (prev.length > 0) {
      if (parsedCurrent.title == prev.at(-1)?.at(0)?.title) {
        const allButLast = prev.slice(0, prev.length - 1);
        const newGroup = prev.at(-1)!.concat(parsedCurrent);
        return [...allButLast, newGroup];
      } else {
        return [...prev, [parsedCurrent]];
      }
    }
    return [[parsedCurrent]];
  }, []);
};

const getBase64FromImageUrl = async (imageUrl: string): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // Ensure cross-origin requests are allowed
    img.src = imageUrl;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext("2d");
      if (ctx == null) {
        reject(new Error("Failed to get 2D context"));
        return;
      }

      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL("image/png"); // Default to PNG
        const base64 = dataURL.split(",")[1]; // Extract Base64 part
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (error) => {
      reject(new Error(`Failed to load image: ${error}`));
    };
  });
};

/**
 * Filters an object to only include keys with non-empty string values
 * Using a functional programming approach
 * @param {Object} obj - The input object to filter
 * @return {Object} A new object with only non-empty string values
 */
const filterNonEmptyStrings = (obj: Record<string, any>): Record<string, any> =>
  Object.entries(obj).reduce((result, [key, value]) => {
    // For strings, only include if non-empty after trimming
    if (typeof value === "string" && value.trim() !== "") {
      return { ...result, [key]: value };
    }
    // Skip empty strings, null, undefined
    return result;
  }, {});

/**
 * Processes a UI hierarchy and multiplies all numerical values by a given multiplier
 * @param data - The UI hierarchy data
 * @param multiplier - The value to multiply numbers by
 * @returns The processed UI hierarchy with multiplied numbers
 */
const multiplyNumbersInUIHierarchy = (data: any, multiplier: number): any => {
  const isNumber = (value: any): boolean =>
    typeof value === "number" && !isNaN(value);

  // Process an object recursively
  const processObject = (obj: any): any => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => processObject(item));
    }

    // Process object properties
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (isNumber(value)) {
        // Multiply numeric values and round down to nearest integer
        result[key] = Math.floor(value * multiplier);
      } else if (typeof value === "object") {
        // Recursively process nested objects
        result[key] = processObject(value);
      } else {
        // Keep non-numeric values as is
        result[key] = value;
      }
    }
    return result;
  };

  return processObject(data);
};

const callChangeProxyState = async (
  proxyAddress: string | undefined,
  enable: boolean
) => {
  const match = proxyAddress?.match(/^https?:\/\/(\d+\.\d+\.\d+\.\d+)/);
  const ipAddress = match != null ? match[1] : null;

  if (ipAddress != null) {
    console.log(`>> Going ${enable ? "online" : "offline"} on ${ipAddress}`);

    try {
      const result = await axios.post(
        `${FASTAPI_SERVER_URL}/change_proxy_state`,
        {
          ip_address: ipAddress,
          enable,
        }
      );
      const returnValue = {
        status: result.status,
        data: result.data,
        statusText: result.statusText,
      };
      console.log(">> callChangeProxyState return value", { returnValue });

      return returnValue;
    } catch (e) {
      console.log(`Error while calling change_proxy_state: ${e}`);
      throw new Error(`Changing offline/online mode has failed`);
    }
  } else {
    throw new Error(`Incorrect proxy address format: ${proxyAddress}`);
  }
};

const updateDeviceHubServerStatus = async (
  ipAddress: string,
  status: string,
  organisationId: string
) => {
  const deviceHubUrl = getDeviceHubUrl(organisationId);
  try {
    return await axios.post(`${deviceHubUrl}/update_status`, {
      ip_address: ipAddress,
      status,
      ...(organisationId != null ? { organisation_id: organisationId } : {}),
    });
  } catch (e) {
    console.error(`Error while calling ${deviceHubUrl}/update_status: ${e}`);
  }
};

async function objectHash(obj) {
  const str = JSON.stringify(obj);
  const msgBuffer = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

const getSessionMutationAtom = (
  deviceProviderSettings?: DeviceProviderSettings
) => {
  switch (deviceProviderSettings?.provider) {
    case "browserstack":
      return appiumSessionMutationAtom;
    case "gptdriver":
      return sessionMutationAtom;
    default:
      return sessionMutationAtom;
  }
};

const shouldUseAppetizeClone = ({
  settingsData,
  buildData,
  fromEditor,
}: {
  settingsData: SettingsData;
  buildData: BuildData;
  fromEditor: boolean;
}) => {
  const shouldUse = fromEditor
    ? settingsData.enableAppetizeCloneEditor
    : settingsData.enableAppetizeCloneCloud;

  if (!shouldUse) return false;

  return buildData.platform.toLowerCase() === "ios"
    ? settingsData.enableAppetizeCloneIOS
    : settingsData.enableAppetizeCloneAndroid;
};

const isValidAnonymousRoute = (pathname: string) => {
  const validRoutes = [
    /^\/recording\/[^\/]+\/runs\/[^\/]+$/,
    /^\/gpt-driver\/sessions\/[^\/]+$/,
  ];
  return validRoutes.some((validRoute) => validRoute.test(pathname));
};

const sanitizeFilename = (filename: string) =>
  filename
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const getLatestDisplayedUIElements = async ({
  usingAppetizeClone,
  appetizeCloneSession,
  session,
  allowNonClickableItems = true,
  settingsData,
}: {
  appetizeCloneSession: any;
  session: any;
  usingAppetizeClone: boolean;
  allowNonClickableItems?: boolean;
  settingsData: SettingsData;
}) => {
  let uiElements;
  if (usingAppetizeClone) {
    const unparsedData = await appetizeCloneSession.current?.getUI();
    console.log("getLatestDisplayedUIElements => unparsedData", unparsedData);
    const transformedData = transformUIData(
      unparsedData,
      appetizeCloneSession?.current?.device.platform.toLowerCase() === "ios"
    );
    console.log("transformedData", transformedData);

    const deviceDimensions = appetizeCloneSession?.current?.device.screen;
    uiElements = getVisibleItems({
      elements: transformedData,
      deviceDimensions,
      allowNonClickableItems,
      ignoredElements: settingsData.uiInspector.ignoredElements,
    });
  } else {
    const unparsedData = await session.current.data.getUI({
      timeout: 5000,
    });
    console.log("getLatestDisplayedUIElements => unparsedData", unparsedData);
    const isIOS = session.current.data.app?.platform.toLowerCase() === "ios";
    const transformedData = transformUIData(unparsedData, isIOS);
    console.log(
      "getLatestDisplayedUIElements => transformedData",
      transformedData
    );

    const deviceDimensions = session.current?.data?.device.screen;

    uiElements = getVisibleItems({
      elements: transformedData,
      allowNonClickableItems,
      deviceDimensions,
      ignoredElements: settingsData.uiInspector.ignoredElements,
    });
  }

  console.log(
    "getLatestDisplayedUIElements => final => uiElements",
    uiElements
  );

  return uiElements;
};

const scrollUntilElementVisible = async ({
  searchConfig,
  searchValue,
  isUp,
  hintForModel,
  observer,
  usingAppetizeClone,
  appetizeCloneSession,
  session,
  handleSwipeOrScroll,
  getBase64Screenshot,
  settingsData,
}: {
  searchConfig: { text?: string; targetId?: string };
  searchValue: string;
  isUp: boolean;
  hintForModel: string | undefined;
  observer: Subscriber<unknown>;
  usingAppetizeClone: boolean;
  appetizeCloneSession: any;
  session: any;
  handleSwipeOrScroll: (cmd: string) => Promise<void>;
  getBase64Screenshot: () => Promise<string>;
  settingsData: SettingsData;
}) => {
  const uiElements = await getLatestDisplayedUIElements({
    session,
    appetizeCloneSession,
    usingAppetizeClone,
    settingsData,
  });
  let latestScreenshot = await getBase64Screenshot();
  const initialCheck = await checkElementVisibility({
    elements: uiElements,
    searchConfig,
    screenshot: latestScreenshot,
    isAssertVisible: true,
  });

  const searchType = searchConfig.text ? "text" : "id";
  if (initialCheck.found) {
    console.log(
      `Element with ${searchType} "${searchValue}" is already visible, no scrolling needed`
    );
    return;
  }

  // Element not initially visible, proceed with scrolling
  let found = false;
  let previousElementsHash = await objectHash(uiElements);
  let unchangedScrollCount = 0;
  let currentElements = uiElements;
  const maxScrolls = 30;

  for (let i = 1; i <= maxScrolls; i++) {
    if (observer.closed) {
      return;
    }

    // Scroll and get updated elements
    await handleSwipeOrScroll(`scroll: ${isUp ? "up" : "down"}`);

    currentElements = await getLatestDisplayedUIElements({
      session,
      appetizeCloneSession,
      usingAppetizeClone,
      settingsData,
    });

    // Hash the new elements and compare with previous
    const currentElementsHash = await objectHash(currentElements);
    const elementsChanged = previousElementsHash !== currentElementsHash;

    console.log(`Scroll ${i}: Elements changed: ${elementsChanged}`);

    // Track consecutive unchanged scrolls
    if (!elementsChanged) {
      unchangedScrollCount++;
      console.log(`UI unchanged after scroll: ${unchangedScrollCount}/3`);

      // Only break after 3 consecutive unchanged scrolls
      if (unchangedScrollCount >= 3) {
        console.log(
          "UI didn't change after 3 consecutive scrolls, stopping search"
        );
        break;
      }
    } else {
      // Reset counter when UI changes
      unchangedScrollCount = 0;
    }

    // Update for next comparison
    previousElementsHash = currentElementsHash;

    latestScreenshot = await getBase64Screenshot();
    // Check element visibility after scroll
    const visibilityCheck = await checkElementVisibility({
      elements: currentElements,
      searchConfig,
      screenshot: latestScreenshot,
      isAssertVisible: true,
    });

    if (visibilityCheck.found) {
      found = true;
      console.log(`Element found after ${i} scroll(s)`);
      break;
    }

    // Add delay for appetize clone
    if (usingAppetizeClone) {
      await delay(500);
    }
  }

  if (!found) {
    const direction = isUp ? "up" : "down";
    if (hintForModel !== undefined && hintForModel.length > 0) {
      throw new Error(
        `UI elements check failed: Asserting ${hintForModel} is visible after scrolling ${direction} ${maxScrolls} times`
      );
    } else {
      throw new Error(
        `Element with ${searchType} "${searchValue}" not found after scrolling ${direction} ${maxScrolls} times`
      );
    }
  }
};

const getIsDetectedByOCR = async ({
  screenshot,
  searchValue,
}: {
  screenshot: string;
  searchValue: string;
}) => {
  try {
    const OCRResponse = await axios.post(
      `${FASTAPI_SERVER_URL}/assert_visible_text`,
      {
        base64_screenshot: screenshot
          .replace("data:image/jpg;base64,", "")
          .trim(),
        text: searchValue,
      }
    );
    return OCRResponse.data.elementFound;
  } catch (e) {
    console.error("Error while calling assert_visible_text: ", e);
    return false;
  }
};

const checkElementVisibility = async ({
  elements,
  searchConfig,
  screenshot,
  isAssertVisible = false,
  shouldPreferClickableItems = false,
}: {
  elements: TreeNode[];
  searchConfig: SearchConfig;
  screenshot?: string;
  isAssertVisible?: boolean;
  shouldPreferClickableItems?: boolean;
}) => {
  // Infer search type and value from config
  const searchType = searchConfig.text ? "text" : "id";
  const searchValue = searchConfig.text ?? searchConfig.targetId ?? "";

  const nodes = findNodeByConfig({
    data: elements,
    config: {
      ...searchConfig,
      isAssertion: isAssertVisible,
    },
  });
  console.log("checkElementVisibility => nodes", nodes);

  const nonOccludedNodes = getNonOccludedElements({
    items: nodes,
    allItems: elements,
  });

  console.log("checkElementVisibility => nonOccludedNodes", nonOccludedNodes);

  if (nonOccludedNodes.length === 0) {
    let detectedByOCR = false;
    if (screenshot && searchType === "text") {
      detectedByOCR = await getIsDetectedByOCR({
        screenshot,
        searchValue,
      });
    }
    return { found: detectedByOCR, element: null };
  }

  if (isAssertVisible) {
    return { found: true, element: null };
  }

  let finalNode = nonOccludedNodes.at(0)!;
  // TODO improve logic to support indexes
  if (nonOccludedNodes.length > 1) {
    nonOccludedNodes.sort(compareDrawingOrder);
    if (shouldPreferClickableItems) {
      const clickableNodes = nonOccludedNodes.filter((node) => node.clickable);

      console.log("checkElementVisibility => clickableNodes", clickableNodes);
      if (clickableNodes.length > 0) {
        const shallowestRelatedNode = findShallowestRelatedNode(clickableNodes);
        if (shallowestRelatedNode) {
          console.log("found shallowestRelatedNode", shallowestRelatedNode);
          finalNode = shallowestRelatedNode;
        } else {
          finalNode = clickableNodes.at(0)!;
        }
      } else {
        finalNode = nonOccludedNodes.at(0)!;
      }
    } else {
      const shallowestRelatedNode = findShallowestRelatedNode(nonOccludedNodes);
      if (shallowestRelatedNode) {
        console.log("found shallowestRelatedNode", shallowestRelatedNode);
        finalNode = shallowestRelatedNode;
      } else {
        console.log(
          "found multiple nodes, picking the first one:",
          nonOccludedNodes,
          nonOccludedNodes.at(0)
        );
        finalNode = nonOccludedNodes.at(0)!;
      }
    }
  }

  return {
    found: true,
    element: finalNode,
  };
};

function normalizeText(text: string): string {
  return text
    .normalize("NFKC") // Keep this - handles tons of edge cases
    .replace("\n", "")
    .replace(/['`´''‚‛′’]/g, "'")
    .replace(/[""„‟″]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F]/g, " ") // Visible spaces
    .replace(/[\u200B-\u200F\uFEFF]/g, "") // Remove zero-width chars
    .replace(
      /[\uE000-\uF8FF]|[\u{F0000}-\u{FFFFD}]|[\u{100000}-\u{10FFFD}]/gu,
      ""
    )
    .trim();
}

const getDeviceHubUrl = (organisationId: string) => {
  return `${FASTAPI_SERVER_URL}/new_device_hub`;
};

function scaleXMLCoordinates(
  xmlString: string,
  scaleFactor: number,
  platform = "auto"
) {
  // Parse the XML string
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "text/xml");

  // Auto-detect platform if not specified
  if (platform === "auto") {
    const rootElement = xmlDoc.documentElement;
    if (
      rootElement.tagName === "AppiumAUT" ||
      rootElement.tagName.startsWith("XCUIElement")
    ) {
      platform = "ios";
    } else if (
      rootElement.tagName === "hierarchy" ||
      rootElement.querySelector("[bounds]")
    ) {
      platform = "android";
    }
  }

  // iOS coordinate attributes (Appium iOS format)
  const iosCoordAttrs = ["x", "y", "width", "height"];

  // Function to scale Android bounds format: [x1,y1][x2,y2]
  function scaleAndroidBounds(boundsStr) {
    const match = boundsStr.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
    if (match) {
      const x1 = Math.round(parseInt(match[1]) * scaleFactor);
      const y1 = Math.round(parseInt(match[2]) * scaleFactor);
      const x2 = Math.round(parseInt(match[3]) * scaleFactor);
      const y2 = Math.round(parseInt(match[4]) * scaleFactor);
      return `[${x1},${y1}][${x2},${y2}]`;
    }
    return boundsStr;
  }

  // Function to recursively process all elements
  function scaleElement(element) {
    if (platform === "ios" || platform === "auto") {
      // Scale iOS coordinate attributes (separate x, y, width, height)
      iosCoordAttrs.forEach((attr) => {
        if (element.hasAttribute(attr)) {
          const value = parseFloat(element.getAttribute(attr));
          const scaledValue = Math.round(value * scaleFactor);
          element.setAttribute(attr, scaledValue.toString());
        }
      });
    }

    if (platform === "android" || platform === "auto") {
      // Scale Android bounds attribute
      if (element.hasAttribute("bounds")) {
        const bounds = element.getAttribute("bounds");
        const scaledBounds = scaleAndroidBounds(bounds);
        element.setAttribute("bounds", scaledBounds);
      }

      // Scale root dimensions (Android hierarchy format)
      if (element.tagName === "hierarchy") {
        if (element.hasAttribute("width")) {
          const width = parseInt(element.getAttribute("width"));
          element.setAttribute(
            "width",
            Math.round(width * scaleFactor).toString()
          );
        }
        if (element.hasAttribute("height")) {
          const height = parseInt(element.getAttribute("height"));
          element.setAttribute(
            "height",
            Math.round(height * scaleFactor).toString()
          );
        }
      }
    }

    // Process child elements
    Array.from(element.children).forEach((child) => {
      scaleElement(child);
    });
  }

  // Start scaling from root element
  scaleElement(xmlDoc.documentElement);

  // Serialize back to string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(xmlDoc);
}

function rescaleDigitsInAppetizeCommands(
  commandsList: string[],
  scale: ScaleMultipliers | null | undefined
): void {
  if (
    scale == null ||
    scale == undefined ||
    (scale.width === 1 && scale.height === 1)
  ) {
    return;
  }

  const tapPattern = /x=(\d+)\s*y=(\d+)/;
  const generalPattern = /;(\d+);(\d+);?/;

  for (let i = 0; i < commandsList.length; i++) {
    const command = commandsList[i];

    for (const pattern of [tapPattern, generalPattern]) {
      const match = command.match(pattern);

      if (match) {
        const oldX = match[1];
        const oldY = match[2];

        const newX = Math.floor(Number(oldX) * scale.width);
        const newY = Math.floor(Number(oldY) * scale.height);

        const newCommand = command
          .replace(oldX, String(newX))
          .replace(oldY, String(newY));

        commandsList[i] = newCommand;
        break;
      }
    }
  }
}

/**
 * Merges launch params from all runs (dependencies + parent).
 * Parent run values take priority on key conflicts.
 * @param runs - Array of run execution data
 * @returns Array of merged launch params (key, value pairs)
 */
const mergeLaunchParams = (
  runs: Array<{
    settings?: { launchParams?: Array<{ key: string; value: string }> };
  }>
): Array<{ key: string; value: string }> => {
  const launchParamsMap = new Map<string, string>();

  // Iterate through all runs, later runs override earlier ones
  runs.forEach((run) => {
    run.settings?.launchParams?.forEach((param) => {
      launchParamsMap.set(param.key, param.value);
    });
  });

  // Convert map back to array
  return Array.from(launchParamsMap.entries()).map(([key, value]) => ({
    key,
    value,
  }));
};

const canUseKeypress = (char: string): boolean => {
  return /^[a-zA-Z0-9 ]$/.test(char);
};

const isUppercaseLetter = (char: string): boolean => {
  return /^[A-Z]$/.test(char);
};

export {
  scaleXMLCoordinates,
  rescaleDigitsInAppetizeCommands,
  parseLocalAppetizeCommand,
  b64toBlob,
  removeItemFromArray,
  replaceItemFomArray,
  capitalizeFirstLetter,
  delay,
  flattenArray,
  getImageDimensions,
  getTemplatesForUploadId,
  handleEmailSending,
  trackAmplitudeEvent,
  trackSentryException,
  trackSentryMessage,
  arrayToCsv,
  downloadBlob,
  invertDirection,
  replaceTemplateItem,
  allStringsEqual,
  getErrorMessage,
  getPropByString,
  annotateScreenshot,
  callEmailReaderAPI,
  getDigitsAfterFirstSemicolon,
  fetchPlus,
  loadAndCropImage,
  compareImages,
  compareStepScreenshots,
  wasAudioPlayingInTheLastThreeSeconds,
  parsePressedKeys,
  bufferToBase64,
  uint8ArrayToBase64,
  getCoordinatesForDevice,
  generateEnrichedPrompts,
  getSlideDirectionAndPercentage,
  getPromptAssistantDeviceCoordinates,
  prepareGetUIElements,
  uploadScreenshotToStorage,
  getBase64ImageResolution,
  curlToAxios,
  executeCurlCommand,
  preRequestStatusOf,
  preRequestErrorMessage,
  getValueByPath,
  getPromptStepsFromTipTapNode,
  createSimpleHtmlList,
  getGptDriverInput,
  timestampToDate,
  replaceEnvVars,
  hideEnvVarSecrets,
  parseGptCommandsResponse,
  imageUrlToBase64,
  getTipTapSchema,
  getTipTapExtensions,
  calculateTotalWeight,
  prepareCachedFlow,
  deleteFirestoreFolder,
  transformTemplates,
  retryOperation,
  getInputValues,
  shortenCommands,
  getExecutionData,
  calculateMinutes,
  getPromptStepsText,
  getStepNumberFromGroup,
  parseCommands,
  parseMarkdownTemplate,
  parsePrompt,
  getRunTemplateTimestamp,
  trackExceptionOnFirestore,
  callGetCachedScreenAPI,
  capitalize,
  getPromptText,
  callDeleteUploadAPI,
  findDuolingoRequest,
  prepareDuolingoAnswerSheet,
  isUnusableCachedScreen,
  promptsEquivalentIgnoringDynamic,
  isAbstractMarkdownPrompt,
  resyncStrictCachingIndex,
  buildMinimizedCachingBaselineForSync,
  redactBase64ForLog,
  buildDynamicValuesForPrompt,
  extractDynamicValuesByAligning,
  templatizeWithDynamicValues,
  callCancelSuiteAPI,
  getFormattedDate,
  findElementAtCoordinatesAndroid,
  findElementAtCoordinatesIOS,
  parseXml,
  generateTapAppiumCodeForiOS,
  generateTapAppiumCodeForAndroid,
  getSlideDirectionAndPercentageFromAssistant,
  generateSwipeOrScrollAppiumCode,
  determineModelVersion,
  poll,
  isLocalCommand,
  CustomMention,
  groupSteps,
  hideNetworkLogsEnvVars,
  withPerfLog,
  parseTestEditorLogs,
  findNodeByConfig,
  getBase64FromImageUrl,
  getBase64ImageDimensions,
  generateRandomInRange,
  replaceEnvVarsAndRunInputs,
  replacePreRequestRememberedValues,
  filterNonEmptyStrings,
  callChangeProxyState,
  rememberNetworkValue,
  updateDeviceHubServerStatus,
  multiplyNumbersInUIHierarchy,
  objectHash,
  getISO8601Date,
  getSessionMutationAtom,
  shouldUseAppetizeClone,
  isValidAnonymousRoute,
  sanitizeFilename,
  downloadAndUploadToStorage,
  getOccludingElement,
  checkElementVisibility,
  scrollUntilElementVisible,
  normalizeText,
  getLatestDisplayedUIElements,
  getDeviceHubUrl,
  getNonOccludedElements,
  resetPerfLogIndent,
  mergeLaunchParams,
  canUseKeypress,
  isUppercaseLetter,
};
